import React, { useState, useEffect, useMemo, useRef, useCallback, Suspense, lazy } from "react";
import { parseDate, getHour, getShift, setDayparts } from "./lib/analytics";
import { api, formatErr } from "./lib/api";
import { AuthProvider, useAuth } from "./lib/auth";
import useAppOffsets from "./lib/useAppOffsets";
import { Overview, Bonus } from "./Dashboard";
import { Customers, Products } from "./DashboardExtras";
import { buildFromCustomerFile, buildCustomersFromInvoices } from "./Customers";
import Login from "./Login";
import UpdateBanner from "./UpdateBanner";
import JustUpdatedToast from "./JustUpdatedToast";
import VersionBadge from "./VersionBadge";
import HeaderUserMenu from "./HeaderUserMenu";
import GlobalSearch from "./GlobalSearch";
import NotificationBell from "./NotificationBell";
import MobileBottomBar from "./MobileBottomBar";
// v11 (2026-02-17) — Iter41: MobileMenuSheet + MobileBottomTabBar removed.
// Only MobileNavDrawer + MobileBottomBar remain (single nav system).
import InstallAppPrompt from "./InstallAppPrompt";
import SplashScreen from "./SplashScreen";
import EnvDot from "./EnvDot";
import DataFreshnessChip from "./DataFreshnessChip";
import UsersAndPermissions from "./UsersAndPermissions";
import PushSettings from "./PushSettings";
import ReportsUploadPanel from "./ReportsUploadPanel";
import AdminNotifyPanel from "./AdminNotifyPanel";
import { PanelErrorBoundary } from "./components/PanelErrorBoundary";
// v119.99 — RetmPanel moved from Settings → "رفع الملفات" to
// Settings → "مركز التكاملات الخارجية" so all integration management
// lives in one place. Import is no longer needed here.
import DigestPanel from "./DigestPanel";
import UploadPanel from "./UploadPanel";
const IntegrationsHubPanel = lazy(() => import("./IntegrationsHubPanel"));
const FieldIntelPanel = lazy(() => import("./FieldIntelPanel"));
const SettingsCompanyIdentity = lazy(() => import("./SettingsCompanyIdentity"));
const MarketingMinAgeSetting = lazy(() => import("./MarketingMinAgeSetting"));
const CategoriesSettings = lazy(() => import("./CategoriesSettings"));
const TechAdvisorPanel = lazy(() => import("./TechAdvisorPanel"));
const EmergentAdvisorPanel = lazy(() => import("./EmergentAdvisorPanel"));
// إلحاق O3-ب2 — xlsx (SheetJS) كانت مستوردة استيراداً ثابتاً أعلى الملف
// فتدخل الحزمة الرئيسية (main chunk) رغم أنها تُستخدَم فقط عند رفع/تنزيل
// ملف Excel (loadXL وdownloadXlTemplate أدناه). حارس:
// test_o3b2_main_bundle_excludes_xlsx.test.js يفشل إن عاد هذا الاستيراد
// ثابتاً، أو تضخّمت main.*.js فوق الحد المسجَّل هناك.
let _xlsxModulePromise = null;
function loadXLSX() {
  if (!_xlsxModulePromise) _xlsxModulePromise = import("xlsx");
  return _xlsxModulePromise;
}

import GoalsPanel from "./GoalsPanel";
import BranchPanel from "./BranchPanel";
import Simulator from "./Simulator";
import WhatsAppPanel from "./WhatsAppPanel";
import SalesAndOps from "./SalesAndOps";
// v119.142 — POS single parent page with internal sub-nav
const POSPage      = lazy(() => import("./POSPage"));
// v119.146 — RETM customers page under العملاء group
const POSCustomers = lazy(() => import("./POSCustomers"));
// Phase 12 — Unified Customer Hub (feature-flagged via REACT_APP_HUB_V2_ENABLED)
const CustomerHub  = lazy(() => import("./customer_intelligence/CustomerHub"));
import PeriodPicker from "./PeriodPicker";
import AIInsights from "./AIInsights";
import Advanced from "./Advanced";
import Inventory from "./Inventory";
import Waste from "./Waste";
import Purchases from "./Purchases";
import Margins from "./Margins";
import CashierCard from "./CashierCard";
import ExpectedToday from "./ExpectedToday";
import CustomerLab from "./CustomerLab";
import Marketing from "./Marketing";
const MarketingHub = lazy(() => import("./MarketingHub"));
const ChannelsHub = lazy(() => import("./ChannelsHub"));
import MarketingConsultant from "./MarketingConsultant";
import MarketingPlansPanel from "./MarketingPlansPanel";
import MarketingGanttPage from "./MarketingGanttPage";
import Campaigns from "./Campaigns";
import CampaignImpact from "./CampaignImpact";
import SocialMedia from "./SocialMedia";
import Events from "./Events";
import Meetings from "./Meetings";
import ErrorsLearning from "./ErrorsLearning";
// Lazy-load the heavier branch & mystery pages so they're only fetched when the user
// opens them. This also defers ~300 lines of JSX/JS off the initial bundle.
const MysteryShopper = lazy(() => import("./MysteryShopper"));
const BranchManager = lazy(() => import("./BranchManager"));
const Finance = lazy(() => import("./Finance"));
const PortalUpload = lazy(() => import("./PortalUpload"));
// v119.90 — Removed the old IntegrationsSettings lazy import.
// The new mounted IntegrationsHubPanel covers the same surface (and more).
const TrainingPage = lazy(() => import("./TrainingPage"));
const HRAcademyManagement = lazy(() => import("./HRAcademyManagement"));
const UploadPortalsPanel = lazy(() => import("./UploadPortalsPanel"));
const AmbassadorsPanel = lazy(() => import("./AmbassadorsPanel"));
const AmbassadorPortal = lazy(() => import("./AmbassadorPortal"));
const CsInboxApp = lazy(() => import("./CsInboxApp"));
const ShortLinkRouter = lazy(() => import("./ShortLinkRouter"));
const BranchScreen = lazy(() => import("./BranchScreen"));
const MyShiftTasks = lazy(() => import("./MyShiftTasks"));
const BranchManagerTasksPanel = lazy(() => import("./BranchManagerTasksPanel"));
const ShiftClose = lazy(() => import("./ShiftClose"));
const ShiftClosuresLog = lazy(() => import("./ShiftClosuresLog"));
const CashDeficitPanel = lazy(() => import("./CashDeficitPanel"));
const FreshnessPanel = lazy(() => import("./FreshnessPanel"));
const Survey360Public = lazy(() => import("./Survey360Public"));
const ThawanyManual = lazy(() => import("./ThawanyManual"));
const AmbassadorCouponsCard = lazy(() => import("./AmbassadorCouponsCard"));
const OperationsManager = lazy(() => import("./OperationsManager"));
const MarketingManager = lazy(() => import("./MarketingManager"));
const ConsultantPage = lazy(() => import("./ConsultantPage"));
const LaylaInstitutePage = lazy(() => import("./LaylaInstitutePage"));
const NotificationRulesUI = lazy(() => import("./NotificationRulesUI"));
const CampaignSchedulerUI = lazy(() => import("./CampaignSchedulerUI"));
const Employees = lazy(() => import("./Employees"));
const QualityReports = lazy(() => import("./QualityReports"));
const CEOPage = lazy(() => import("./CEOPage"));
const SystemDiagnostics = lazy(() => import("./SystemDiagnostics"));
const RequestsPage = lazy(() => import("./RequestsPage"));
const RequestTemplatesPanel = lazy(() => import("./RequestTemplatesPanel"));
const CustomerSurveys = lazy(() => import("./CustomerSurveys"));
const TemplatePickerDemo = lazy(() => import("./TemplatePickerDemo"));
const HRSurveys = lazy(() => import("./HRSurveys"));
const MakeIntegrationPage = lazy(() => import("./MakeIntegrationPage"));
import HomePage from "./HomePage";
import AuditLog from "./AuditLog";
import RbacAuditPanel from "./RbacAuditPanel";
// CeoMeetingsPanel import removed (v119.11) — now rendered inside CEOPage
import MyWorkPanel from "./MyWorkPanel";
import DamageManagementPanel from "./DamageManagementPanel";
import SystemOptimizationPanel from "./SystemOptimizationPanel";
import EventsCalendarPanel from "./EventsCalendarPanel";
import GroupedTabs from "./GroupedTabs";
import MobileNavDrawer from "./MobileNavDrawer";
import CollapsibleSection, { SettingsCatProvider, SettingsTabsBar } from "./CollapsibleSection";
const PermissionBoxesPanel = lazy(() => import("./PermissionBoxesPanel"));
const SOPEnginePanel = lazy(() => import("./SOPEnginePanel"));
import Toaster from "./Toaster";
import { Toaster as SonnerToaster } from "sonner";
import { useTheme } from "./lib/theme";
import { ALL_TABS, ROLE_UPLOAD, roleBadgeFor, canTab } from "./tabs";
import "./App.css";
import "./polish.css";
import "./mobile.css";
import "./mobile_theme.css";
import "./sidebar_responsive.css";
import { formatKSADate, ksaAddDays, ksaStartOfMonth, ksaStartOfWeek, ksaToday } from "./lib/ksaTime";

// Detect the "59 X" marker (customer exhausted their 59 allowance).
// Robust to: invisible RTL marks, multiple kinds of spaces, ASCII X/x, Cyrillic Х/х (U+0425/0445),
// multiplication sign × (U+00D7), and surrounding punctuation.
const is59xRegex = /59[\s\u00A0\u200E\u200F\u202A-\u202E\u2066-\u2069\u061C\-_.،,()\\/×]*[xXхХ×]/;
const detect59x = (s) => is59xRegex.test(String(s || ""));

function Alert({ msg }) {
  if (!msg) return null;
  return <div className={`al al${msg.t}`} data-testid="toast-alert">{msg.m}</div>;
}

function Dashboard() {
  const { user, logout, hasPerm, permissions } = useAuth();
  const [, setDaypartsReady] = useState(0);
  useEffect(() => {  // D1 — حدود فترات اليوم من الإعدادات (getShift)
    if (user) api.get("/pos/dayparts/settings").then((r) => { setDayparts(r.data?.parts); setDaypartsReady((n) => n + 1); }).catch(() => {});
  }, [user]);
  const [theme, toggleTheme] = useTheme();
  useAppOffsets(); // v198 — writes --app-top-offset / --app-hdr-offset / --app-bottom-offset

  // v198 — welcome greeting text sourced from tenant_settings.welcome_texts.
  // No greeting strings live in this file. Empty string on load = safe fallback
  // (banner still renders with only the user's name — never crashes).
  const [welcomeText, setWelcomeText] = useState("");
  useEffect(() => {
    let alive = true;
    api.get("/tips/welcome-texts").then(({ data }) => {
      if (!alive) return;
      const texts = (data && data.texts) || {};
      const roleKey = (user?.role || "viewer").toLowerCase();
      setWelcomeText(texts[roleKey] || texts.viewer || "");
    }).catch(() => {});
    return () => { alive = false; };
  }, [user?.role]);
  const tabsForRole = useMemo(() => {
    // Universal baseline — every authenticated user always sees these,
    // even if admin gave them an empty page_permissions list (v118.37).
    const UNIVERSAL = new Set(["home", "training"]);
    // Legacy `page_permissions` on the user record still wins when set
    // (admin can opt-in any tab for any user — pre-RBAC override).
    if (user.role !== "admin" && Array.isArray(user.page_permissions) && user.page_permissions.length > 0) {
      const allowed = new Set([...user.page_permissions, ...UNIVERSAL]);
      return ALL_TABS.filter((t) => allowed.has(t.id));
    }
    // New model: filter by permission. Falls back to roles[] until the
    // /auth/me/permissions response lands (avoids a flash-of-empty-nav).
    return ALL_TABS.filter((t) => canTab(t, user, hasPerm));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, hasPerm, permissions]);

  // v11 (2026-02-17) — Iter41: groupedTabsForMobile removed with
  // MobileMenuSheet. Orphan coverage now lives inside MobileNavDrawer
  // (see test_mobile_drawer_orphan_coverage.py).
  // v119.122 (Khalid Jul 2026): merge role defaults with per-user granular
  // upload permissions so admin can grant `data.upload.inv`, `data.upload.cust`
  // etc. to any user without changing their role. `data.upload` (broad) enables
  // ALL upload types — matches the RBAC catalog entry.
  const canUpload = useMemo(() => {
    const defaults = ROLE_UPLOAD[user.role] || {};
    const has = (p) => hasPerm(p);
    const broadUpload = has("data.upload");
    return {
      inv:      defaults.inv      || broadUpload || has("data.upload.inv"),
      cust:     defaults.cust     || broadUpload || has("data.upload.cust"),
      prod:     defaults.prod     || broadUpload || has("data.upload.prod"),
      stk:      defaults.stk      || broadUpload || has("data.upload.stk"),
      wst:      defaults.wst      || broadUpload || has("data.upload.wst"),
      cost:     defaults.cost     || broadUpload || has("data.upload.cost"),
      purch:    defaults.purch    || broadUpload || has("data.upload.purch"),
      dsum:     defaults.dsum     || broadUpload || has("data.upload.dsum"),
      mkt_plan: defaults.mkt_plan || broadUpload || has("data.upload.mkt_plan"),
    };
  }, [user.role, hasPerm]);

  const [inv, setInv] = useState([]);
  const [cst, setCst] = useState([]);
  const [prods, setProds] = useState([]);
  const [stk, setStk] = useState([]);
  const [wst, setWst] = useState([]);
  const [costs, setCosts] = useState([]);
  const [purch, setPurch] = useState([]);
  const [dsum, setDsum] = useState([]);
  const [meta, setMeta] = useState({ inv: null, cust: null, prod: null });
  const [period, setPeriod] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  // v119.142 — Persist active tab in URL so refresh keeps user on same page.
  const _initialTab = (() => {
    try {
      // v119.201 — /settings deep link must open the settings tab, never home.
      if (window.location.pathname === "/settings") {
        window.history.replaceState(null, "", "/?tab=st");
        return "st";
      }
      const u = new URL(window.location.href);
      const q = u.searchParams.get("tab");
      if (q) return q;
      const ls = localStorage.getItem("tw_tab");
      if (ls) return ls;
    } catch { /* ignore */ }
    return tabsForRole[0]?.id || "ov";
  })();
  const [tab, _setTab] = useState(_initialTab);
  const setTab = useCallback((next) => {
    _setTab(next);
    try {
      const u = new URL(window.location.href);
      u.searchParams.set("tab", next);
      window.history.replaceState(null, "", u.toString());
      localStorage.setItem("tw_tab", next);
    } catch { /* ignore */ }
  }, []);
  const [msg, setMsg] = useState(null);
  const [stMsg, setStMsg] = useState(null);
  const [loaded, setLoaded] = useState(false);

  // Listen for custom navigation events (e.g., from CEO smart alerts)
  useEffect(() => {
    const handler = (e) => {
      const t = e?.detail?.tab;
      if (t) setTab(t);
    };
    window.addEventListener("tw-navigate", handler);
    return () => window.removeEventListener("tw-navigate", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const fiRef = useRef(null);
  const fcRef = useRef(null);
  const fpRef = useRef(null);
  const fsRef = useRef(null);
  const fwRef = useRef(null);
  const fkRef = useRef(null);
  const fpurRef = useRef(null);
  const fmktRef = useRef(null);
  const fmktPlanRef = useRef(null);
  const fdsRef = useRef(null);
  const fjRef = useRef(null);

  // Load data once authed
  const loadData = useCallback(async () => {
    try {
      const [{ data }, { data: m }] = await Promise.all([
        api.get("/data/all"),
        api.get("/data/meta").catch(() => ({ data: {} })),
      ]);
      setInv(data.inv || []);
      setCst(data.cust || []);
      setProds(data.prod || []);
      setStk(data.stk || []);
      setWst(data.wst || []);
      setCosts(data.cost || []);
      setPurch(data.purch || []);
      setDsum(data.dsum || []);
      setMeta(m || {});
    } catch (e) { /* noop */ }
  }, []);

  useEffect(() => {
    (async () => {
      await loadData();
      setLoaded(true);
    })();
  }, [loadData]);

  // Auto-refresh every 60s if data was sourced from Retm (live)
  useEffect(() => {
    const liveSource = ["inv", "cust", "prod"].some((k) => meta?.[k]?.source === "retm");
    if (!liveSource) return undefined;
    const t = setInterval(() => { loadData(); }, 60_000);
    return () => clearInterval(t);
  }, [meta, loadData]);

  // v119.99 — Allow distant panels (e.g. IntegrationsHubPanel.RetmPanel)
  // to trigger a data refresh without prop drilling.
  useEffect(() => {
    const handler = () => { loadData(); };
    window.addEventListener("tw-data-refresh", handler);
    return () => window.removeEventListener("tw-data-refresh", handler);
  }, [loadData]);

  // v119.102 — Poll the count of "requests waiting for MY decision" every
  // 60s so the navbar badge stays fresh. Renders a red 🔴 dot on the
  // 📋 الطلبات group → users never miss approvals.
  const [pendingReqsCount, setPendingReqsCount] = useState(0);
  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const { data } = await api.get("/requests?view=pending&limit=1");
        if (!cancelled) setPendingReqsCount(Number(data?.count || 0));
      } catch { /* silent — non-critical badge */ }
    };
    fetchCount();
    const t = setInterval(fetchCount, 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, [user]);

  // Initial date bounds
  useEffect(() => {
    if (!inv.length) return;
    const dates = inv.map((r) => r.date).sort();
    setDateFrom(dates[0]);
    setDateTo(dates[dates.length - 1]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inv.length]);

  // Note: each chart is destroyed-before-create inside mkC() so no preemptive cleanup needed.
  // Previously we called destroyAll() on tab change here, but React fires child effects BEFORE
  // parent effects, which meant charts were destroyed immediately after creation. Removed.

  const fil = useMemo(() => {
    if (!inv.length) return [];
    // Local-time YYYY-MM-DD formatter (avoids UTC shift in Saudi +3)
    // ⑤/L3-أ — بتوقيت السعودية عبر lib/ksaTime.js، وبداية الأسبوع إعداد.
    const td = ksaToday();
    const yd = ksaAddDays(td, -1);
    const wk = ksaAddDays(td, -7);
    const wk30 = ksaAddDays(td, -30);
    const ms = ksaStartOfMonth(td);
    const prevMonthEnd = ksaAddDays(ms, -1);
    const prevMonthStart = ksaStartOfMonth(prevMonthEnd);
    const wkStart = ksaStartOfWeek(td);
    const prevWkEnd = ksaAddDays(wkStart, -1);
    const prevWkStart = ksaAddDays(wkStart, -7);
    let fr = "", to = td;
    if (period === "today") { fr = td; to = td; }
    else if (period === "yesterday") { fr = yd; to = yd; }
    else if (period === "week") fr = wk;
    else if (period === "week30") fr = wk30;
    else if (period === "thisweek") fr = wkStart;
    else if (period === "prevweek") { fr = prevWkStart; to = prevWkEnd; }
    else if (period === "month") fr = ms;
    else if (period === "prevmonth") { fr = prevMonthStart; to = prevMonthEnd; }
    else if (period === "custom") { fr = dateFrom; to = dateTo; }
    return inv.filter((r) => (!fr || r.date >= fr) && (!to || r.date <= to));
  }, [inv, period, dateFrom, dateTo]);

  const showMsg = (t, m, d = 5000) => {
    setMsg({ t, m });
    if (d) setTimeout(() => setMsg(null), d);
  };

  const persist = async (type, items) => {
    try {
      const { data } = await api.post(`/data/${type}`, { items, mode: "merge" });
      // Show merge feedback for incremental upload
      if (data && data.mode === "merge" && (data.added !== undefined)) {
        const parts = [];
        if (data.added > 0) parts.push(`✅ أُضيف ${data.added.toLocaleString("en-US")}`);
        if (data.updated > 0) parts.push(`🔄 حُدّث ${data.updated.toLocaleString("en-US")}`);
        if (data.duplicates > 0) parts.push(`⏭ تخطّى ${data.duplicates.toLocaleString("en-US")} مكرراً`);
        if (parts.length) showMsg("g", parts.join(" · "), 7000);
      }
    } catch (e) {
      showMsg("r", formatErr(e.response?.data?.detail) || "تعذّر حفظ البيانات");
    }
  };

  // v119.244 — Excel template definitions per upload type (support fix #2).
  const XL_TEMPLATES = {
    inv:  { name: "نموذج_الفواتير", required: "التاريخ، الإجمالي (اختياري: العميل، الجوال، الخصم، رمز الخصم)",
            headers: ["التاريخ", "العميل", "الجوال", "الإجمالي", "الخصم", "رمز الخصم"],
            example: ["2026-06-01 10:30", "أحمد محمد", "0555123456", 35.5, 0, ""] },
    cust: { name: "نموذج_العملاء", required: "الاسم، الهاتف، الزيارات",
            headers: ["الاسم", "الهاتف", "الزيارات"],
            example: ["أحمد محمد", "0555123456", 4] },
    prod: { name: "نموذج_المنتجات", required: "الصنف + أعمدة المبيعات (صافي المبيعات مع الضريبة، الكمية الصافية…)",
            headers: ["الصنف", "المجموع مع الضريبة", "المبيعات بدون ضريبة", "صافي المبيعات مع الضريبة", "صافي المبيعات", "الضرائب", "قيمة الخصم", "عدد المبيعات", "متوسط الفاتورة", "عدد العملاء", "الكمية الصافية", "التكلفة", "قيمة الإرجاع", "عدد الإرجاعات", "قيمة الإلغاء", "عدد الإلغاءات", "الربح التقريب", "الرسوم"],
            example: ["لاتيه", 1150, 1000, 1150, 1000, 150, 0, 100, 11.5, 90, 100, 400, 0, 0, 0, 0, 600, 0] },
    dsum: { name: "نموذج_الملخص_اليومي", required: "تاريخ البيع، عدد المبيعات",
            headers: ["تاريخ البيع", "المجموع مع الضريبة", "المبيعات بدون ضريبة", "صافي المبيعات مع الضريبة", "صافي المبيعات", "الضرائب", "قيمة الخصم", "عدد المبيعات", "متوسط الفاتورة", "عدد العملاء", "الكمية الصافية", "التكلفة", "قيمة الإرجاع", "عدد الإرجاعات", "قيمة الإلغاء", "عدد الإلغاءات", "الربح التقريب", "الرسوم"],
            example: ["2026-06-01", 5000, 4348, 5000, 4348, 652, 120, 180, 27.8, 160, 200, 1800, 50, 2, 0, 0, 2500, 0] },
    stk:  { name: "نموذج_المخزون", required: "الاسم، إجمالي الكمية",
            headers: ["المعرف", "رمز المنتج", "الاسم", "وحدة التخزين", "وحدة الاستهلاك", "المضاعف", "إجمالي الكمية", "إجمالي الكمية المخطط", "إجمالي الكمية المحجوزة"],
            example: ["1001", "PRD-01", "حليب طازج", "كرتون", "لتر", 12, 40, 60, 0] },
    wst:  { name: "نموذج_التالف", required: "الاسم، إجمالي الكمية",
            headers: ["المعرف", "رمز المنتج", "الاسم", "وحدة التخزين", "وحدة الاستهلاك", "المضاعف", "إجمالي الكمية", "إجمالي الكمية المخطط", "إجمالي الكمية المحجوزة"],
            example: ["1001", "PRD-01", "حليب طازج", "كرتون", "لتر", 12, 3, 0, 0] },
    cost: { name: "نموذج_التكلفة", required: "اسم المنتج، التكلفة",
            headers: ["المعرف", "اسم المنتج", "التكلفة", "السعر", "الفئة", "الحالة", "ملاحظة"],
            example: ["1001", "لاتيه", 4.2, 14, "مشروبات ساخنة", "نشط", ""] },
    purch: { name: "نموذج_المشتريات", required: "اسم المورد، تاريخ الفاتورة، القيمة شامل الضريبة",
            headers: ["المرجع", "اسم المورد", "الرقم الضريبى للمورد", "رقم الفاتورة", "تاريخ الفاتورة", "تاريخ الانشاء", "القيمة قبل الضريبة", "الضريبة", "القيمة شامل الضريبة"],
            example: ["PO-100", "مورد البن", "310000000000003", "INV-55", "2026-06-01", "2026-06-01", 1000, 150, 1150] },
  };

  const downloadXlTemplate = async (tp) => {
    const t = XL_TEMPLATES[tp];
    if (!t) return;
    const XLSX = await loadXLSX();
    const ws = XLSX.utils.aoa_to_sheet([t.headers, t.example]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "البيانات");
    XLSX.writeFile(wb, `${t.name}.xlsx`);
  };

  const loadXL = (file, tp) => {
    if (!file) return;
    if (!canUpload[tp]) {
      showMsg("r", "ليس لديك صلاحية رفع هذا النوع من الملفات");
      return;
    }
    const r = new FileReader();
    r.onload = async (e) => {
      try {
        const XLSX = await loadXLSX();
        const wb = XLSX.read(e.target.result, { type: "array", cellDates: true });

        // Marketing workbook has multiple sheets; we read "المختصر" specifically.
        // Layout differs from invoice/customer/product files, so handle it before
        // the generic header detection.
        if (tp === "mkt") {
          const sheetName = wb.SheetNames.find((n) => n.includes("المختصر")) || wb.SheetNames[0];
          const wsM = wb.Sheets[sheetName];
          const rowsM = XLSX.utils.sheet_to_json(wsM, { header: 1, defval: null, raw: false });
          let hrM = 0;
          for (let i = 0; i < Math.min(rowsM.length, 6); i++) {
            const txt = rowsM[i].map((c) => String(c || "").trim().toLowerCase());
            if (txt.some((c) => c.includes("الاسبوع"))) { hrM = i; break; }
          }
          const hdM = rowsM[hrM].map((c) => String(c || "").trim());
          const dataM = rowsM.slice(hrM + 1).filter((row) => row.some((v) => v !== null && v !== ""));
          const ciM = (...candidates) => {
            for (const cand of candidates) {
              const idx = hdM.findIndex((h) => h.toLowerCase().includes(cand.toLowerCase()));
              if (idx !== -1) return idx;
            }
            return -1;
          };
          const cols = {
            week: ciM("الاسبوع", "الأسبوع"),
            total_views: ciM("اجمالي المشاهدات"),
            total_posts: ciM("اجمالي المنشورات"),
            total_new_followers: ciM("اجمالي المتابعين الجدد"),
            total_followers: ciM("اجمالي المتابعين"),
            snapchat_posts: ciM("عدد المنشورات"),
            snapchat_story_views: ciM("عدد مشاهدات اللقطة"),
            snapchat_viewers: ciM("المشاهدون"),
            snapchat_avg_watch_min: ciM("متوسط وقت المشاهدة"),
            snapchat_total_watch_hr: ciM("اجمالي وقت المشاهدة"),
            snapchat_followers: ciM("عدد المتابعين"),
            snapchat_new_followers: ciM("عدد المتابعين الجدد"),
            snapchat_profile_views: ciM("عدد مشاهدات الملف التعريفي"),
            instagram_reached: ciM("Reached"),
            instagram_engagements: ciM("Instagram interactions", "Instagram Engagements"),
            instagram_views: ciM("Instagram Views"),
            instagram_followers: ciM("Instagram Followers"),
            instagram_unfollow: ciM("instagram unfollow"),
            instagram_new_followers: ciM("instagram New Followers"),
            instagram_posts: ciM("instagram Post"),
            instagram_reels: ciM("Instagram Reels"),
            instagram_stories: ciM("Instagram Story"),
            tiktok_profile_views: ciM("Tik tok profile views"),
            tiktok_post_views: ciM("post views"),
            tiktok_followers: ciM("Followers"),
            tiktok_unfollow: ciM("Tik tok unfollow"),
            tiktok_new_followers: ciM("Tik tok New Followers"),
            tiktok_impressions: ciM("Tik tok Impressions"),
            twitter_engagements: ciM("X Engagements"),
            twitter_profile_visits: ciM("X profile visits"),
            twitter_unfollow: ciM("X unfollow"),
            twitter_new_posts: ciM("X New Post"),
            twitter_new_followers: ciM("X New Followers"),
            twitter_views: ciM("X Views"),
            linktree_clicks: ciM("link tree Clicks"),
            linktree_search_appearance: ciM("Link tree search appearance"),
          };
          if (cols.week === -1) { showMsg("r", "⚠ تأكد أن الورقة 'المختصر' فيها عمود 'الاسبوع'"); return; }
          const num = (v) => {
            if (v === null || v === undefined || v === "") return 0;
            const n = Number(String(v).replace(/,/g, ""));
            return Number.isFinite(n) ? n : 0;
          };
          const today = new Date();
          const items = dataM
            .filter((row) => row[cols.week] !== null && row[cols.week] !== "")
            .map((row, idx, arr) => {
              const weekDate = ksaAddDays(ksaToday(today), -(arr.length - 1 - idx) * 7);
              const get = (k) => cols[k] >= 0 ? num(row[cols[k]]) : 0;
              return {
                date: weekDate,
                notes: `أسبوع ${row[cols.week]}`,
                instagram_followers: get("instagram_followers"),
                tiktok_followers: get("tiktok_followers"),
                snapchat_followers: get("snapchat_followers") || get("snapchat_new_followers"),
                twitter_followers: 0,
                linktree_visits: get("linktree_search_appearance"),
                linktree_clicks: get("linktree_clicks"),
                engagement_likes: get("instagram_engagements"),
                engagement_comments: 0,
                instagram_views: get("instagram_views"),
                instagram_reached: get("instagram_reached"),
                instagram_posts: get("instagram_posts"),
                instagram_reels: get("instagram_reels"),
                instagram_stories: get("instagram_stories"),
                instagram_new_followers: get("instagram_new_followers"),
                instagram_unfollow: get("instagram_unfollow"),
                instagram_engagements: get("instagram_engagements"),
                tiktok_post_views: get("tiktok_post_views"),
                tiktok_profile_views: get("tiktok_profile_views"),
                tiktok_impressions: get("tiktok_impressions"),
                tiktok_new_followers: get("tiktok_new_followers"),
                tiktok_unfollow: get("tiktok_unfollow"),
                snapchat_posts: get("snapchat_posts"),
                snapchat_story_views: get("snapchat_story_views"),
                snapchat_viewers: get("snapchat_viewers"),
                snapchat_avg_watch_min: get("snapchat_avg_watch_min"),
                snapchat_total_watch_hr: get("snapchat_total_watch_hr"),
                snapchat_profile_views: get("snapchat_profile_views"),
                snapchat_new_followers: get("snapchat_new_followers"),
                twitter_engagements: get("twitter_engagements"),
                twitter_profile_visits: get("twitter_profile_visits"),
                twitter_views: get("twitter_views"),
                twitter_new_followers: get("twitter_new_followers"),
                twitter_new_posts: get("twitter_new_posts"),
                twitter_unfollow: get("twitter_unfollow"),
                linktree_search_appearance: get("linktree_search_appearance"),
                total_views: get("total_views"),
                total_posts: get("total_posts"),
                total_followers: get("total_followers"),
                total_new_followers: get("total_new_followers"),
              };
            });
          if (!items.length) { showMsg("r", "⚠ لم يتم العثور على صفوف أسبوعية في الملف"); return; }
          api.post("/marketing/snapshots/bulk", { items })
            .then(({ data }) => {
              showMsg("g", `✅ ${data.saved} أسبوع تم استيراده من ملف التسويق${data.skipped ? ` (تخطّي ${data.skipped})` : ""}`);
            })
            .catch((err) => {
              showMsg("r", formatErr(err.response?.data?.detail) || err.message);
            });
          return;
        }

        const ws = wb.Sheets[wb.SheetNames[0]];
        const raw = XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, defval: "" });
        let hr = -1;
        for (let i = 0; i < Math.min(6, raw.length); i++) {
          const r2 = raw[i].map((c) => String(c));
          if (r2.some((c) => c.includes("التاريخ") || c.includes("الاسم") || c.includes("الصنف") || c.includes("الفاتورة") || c.includes("رمز المنتج") || c.includes("المعرف") || c.includes("المرجع") || c.includes("المورد") || c.includes("تاريخ البيع"))) { hr = i; break; }
        }
        if (hr < 0) {
          // v119.244 — diagnostic aid: dump actual file headers to console +
          // tell the user exactly which columns are expected.
          console.warn(`[loadXL] type=${tp} — لم يتم التعرف على رأس الجدول. أول 3 صفوف من الملف:`, raw.slice(0, 3));
          const tpl = XL_TEMPLATES[tp];
          showMsg("r", `⚠ لم يتعرف على تنسيق الملف${tpl ? ` — الأعمدة المطلوبة: ${tpl.required}. حمّل «نموذج إكسل» من صفحة الرفع للمقارنة.` : ""}`, 10000);
          return;
        }
        const hd = raw[hr].map((h) => String(h).trim());
        console.log(`[loadXL] type=${tp} — الأعمدة المكتشفة في الملف:`, hd);
        const ci = (n) => hd.findIndex((h) => h.includes(n));
        const rows = raw.slice(hr + 1).filter((row) => row.some((v) => v !== ""));

        if (tp === "dsum") {
          // Daily Sales Summary report from POS — re-read sheet with raw:true so numeric cells come
          // through as JavaScript numbers (not formatted strings). This avoids accounting-format
          // negatives like (11.78) and RTL-mark contamination.
          const rawN = XLSX.utils.sheet_to_json(ws, { header: 1, raw: true, defval: null });
          let hr2 = -1;
          for (let i = 0; i < Math.min(6, rawN.length); i++) {
            const r2 = rawN[i].map((c) => String(c));
            if (r2.some((c) => c.includes("تاريخ البيع"))) { hr2 = i; break; }
          }
          if (hr2 < 0) {
            showMsg("r", "⚠ لم يتعرف على تنسيق ملف الملخص اليومي");
            return;
          }
          const hdN = rawN[hr2].map((h) => String(h).trim());
          const rowsN = rawN.slice(hr2 + 1).filter((row) => row.some((v) => v !== null && v !== ""));
          // Daily Sales Summary report from POS — authoritative source for daily totals.
          // Headers (Arabic): تاريخ البيع | المجموع مع الضريبة | المبيعات بدون ضريبة |
          //   صافي المبيعات مع الضريبة | صافي المبيعات | الضرائب | قيمة الخصم | عدد المبيعات |
          //   متوسط الفاتورة | عدد العملاء | الكمية الصافية | التكلفة | قيمة الإرجاع |
          //   عدد الإرجاعات | قيمة الإلغاء | عدد الإلغاءات | الربح التقريب | الرسوم
          // Normalize header text: strip RTL marks, collapse whitespace.
          const _norm = (s) => String(s || "")
            .replace(/[\u200E\u200F\u202A-\u202E\u2066-\u2069\u061C]/g, "")
            .replace(/\s+/g, " ")
            .trim();
          const hdNorm = hdN.map(_norm);
          // Use exact-match lookup to avoid substring collisions (e.g. "صافي المبيعات" matching "صافي المبيعات مع الضريبة").
          const cx = (target) => hdNorm.findIndex((h) => h === _norm(target));
          const iDate = cx("تاريخ البيع") >= 0 ? cx("تاريخ البيع") : hdNorm.findIndex((h) => h.includes("التاريخ"));
          const iGross  = cx("المجموع مع الضريبة");
          const iSalesNoTax = cx("المبيعات بدون ضريبة");
          const iNetTax = cx("صافي المبيعات مع الضريبة");
          const iNet    = cx("صافي المبيعات");
          const iTax    = cx("الضرائب");
          const iDisc   = cx("قيمة الخصم");
          const iCount  = cx("عدد المبيعات");
          const iAvg    = cx("متوسط الفاتورة");
          const iCust   = cx("عدد العملاء");
          const iQty    = cx("الكمية الصافية");
          const iCost   = cx("التكلفة");
          const iRetV   = cx("قيمة الإرجاع");
          const iRetN   = cx("عدد الإرجاعات");
          const iCanV   = cx("قيمة الإلغاء");
          const iCanN   = cx("عدد الإلغاءات");
          // "الربح التقريب" may appear as a single header or split into two cells ("الربح" + "التقريب").
          // The numeric value is in the first cell, so match on "الربح" alone.
          const iProfit = cx("الربح التقريب") >= 0 ? cx("الربح التقريب")
                        : cx("الربح التقريبي") >= 0 ? cx("الربح التقريبي")
                        : hdNorm.findIndex((h) => h === "الربح" || h.startsWith("الربح "));
          const iFees   = cx("الرسوم");
          if (iDate < 0 || iCount < 0) {
            showMsg("r", "⚠ تأكد من وجود: تاريخ البيع وعدد المبيعات");
            return;
          }
          // Number parser robust to RTL marks, Unicode minus, commas, accounting parens (123) → -123.
          const num = (v) => {
            let s = String(v ?? "").trim();
            if (!s) return 0;
            // Strip RTL/LTR marks
            s = s.replace(/[\u200E\u200F\u202A-\u202E\u2066-\u2069\u061C]/g, "");
            // Normalize Unicode minus to ASCII hyphen
            s = s.replace(/[\u2212\u2010\u2011\u2013\u2014]/g, "-");
            // Accounting-format negatives: (123.45) → -123.45
            const parenMatch = s.match(/^\(([^)]+)\)$/);
            if (parenMatch) s = "-" + parenMatch[1];
            // Remove thousands separators (commas, Arabic thousands U+066C, regular spaces, NBSP, Arabic comma)
            s = s.replace(/[,،\u066C\s\u00A0]/g, "");
            // Convert Arabic-Indic digits to ASCII
            s = s.replace(/[\u0660-\u0669]/g, (c) => String(c.charCodeAt(0) - 0x0660));
            s = s.replace(/[\u06F0-\u06F9]/g, (c) => String(c.charCodeAt(0) - 0x06F0));
            const n = parseFloat(s);
            return Number.isFinite(n) ? n : 0;
          };
          const out = [];
          rowsN.forEach((row) => {
            const dateRaw = row[iDate];
            const { d } = parseDate(dateRaw);
            if (!d) return;
            // Skip a possible "total" row at the bottom (no real date)
            const cnt = num(row[iCount]);
            if (cnt <= 0 && num(row[iGross]) <= 0) return;
            out.push({
              date: d,
              gross:        iGross  >= 0 ? num(row[iGross])  : 0,
              sales_no_tax: iSalesNoTax >= 0 ? num(row[iSalesNoTax]) : 0,
              net_with_tax: iNetTax >= 0 ? num(row[iNetTax]) : 0,
              net:          iNet    >= 0 ? num(row[iNet])    : 0,
              tax:          iTax    >= 0 ? num(row[iTax])    : 0,
              disc:         iDisc   >= 0 ? num(row[iDisc])   : 0,
              count:        cnt,
              avg_invoice:  iAvg    >= 0 ? num(row[iAvg])    : 0,
              customers:    iCust   >= 0 ? num(row[iCust])   : 0,
              qty:          iQty    >= 0 ? num(row[iQty])    : 0,
              cost:         iCost   >= 0 ? num(row[iCost])   : 0,
              return_value: iRetV   >= 0 ? num(row[iRetV])   : 0,
              return_count: iRetN   >= 0 ? num(row[iRetN])   : 0,
              cancel_value: iCanV   >= 0 ? num(row[iCanV])   : 0,
              cancel_count: iCanN   >= 0 ? num(row[iCanN])   : 0,
              profit:       iProfit >= 0 ? num(row[iProfit]) : 0,
              fees:         iFees   >= 0 ? num(row[iFees])   : 0,
            });
          });
          if (!out.length) { showMsg("r", "⚠ لا توجد صفوف صالحة في الملف"); return; }
          out.sort((a, b) => a.date.localeCompare(b.date));
          setDsum(out);
          persist("dsum", out);
          const totalNet = out.reduce((s, r) => s + r.net_with_tax, 0);
          const totalCnt = out.reduce((s, r) => s + r.count, 0);
          showMsg("g", `✅ ${out.length} يوم — صافي ${totalNet.toLocaleString("en-US", { maximumFractionDigits: 0 })} ر · ${totalCnt.toLocaleString("en-US")} فاتورة`);
          return;
        }

        if (tp === "inv") {
          const iD = ci("التاريخ"), iT = ci("الإجمالي"), iX = ci("الخصم");
          let iC = ci("العميل");
          let iPH = ci("الجوال") >= 0 ? ci("الجوال") : (ci("الهاتف") >= 0 ? ci("الهاتف") : ci("رقم الجوال"));
          // Coupon/discount code column (RETM may export as "رمز الخصم", "كود الخصم",
          // "اسم الخصم", or "الكوبون"). We surface this so we can later cross-reference
          // ambassadors with the per-invoice visit log.
          const iDC = ci("رمز الخصم") >= 0 ? ci("رمز الخصم")
                    : ci("كود الخصم") >= 0 ? ci("كود الخصم")
                    : ci("اسم الخصم") >= 0 ? ci("اسم الخصم")
                    : ci("الكوبون") >= 0 ? ci("الكوبون")
                    : ci("الكود") >= 0 ? ci("الكود")
                    : -1;
          if (iD < 0 || iT < 0) { showMsg("r", "⚠ تأكد من وجود: التاريخ والإجمالي"); return; }
          // ─── Content-aware phone/name auto-detection ───
          // Retm sometimes ships invoice exports where headers and data columns are
          // misaligned (e.g. "العميل" header points to a column that actually contains
          // 9-digit phones). Inspect the first 50 rows: if the "العميل" column is mostly
          // digit-only (>=8 chars), it's actually the phone column. The real name is one
          // column to the LEFT.
          const sample = rows.slice(0, Math.min(50, rows.length));
          if (iC >= 0 && sample.length) {
            const looksPhone = (v) => {
              const s = String(v ?? "").replace(/\D/g, "");
              return s.length >= 8 && s.length <= 12;
            };
            const isArabicName = (v) => {
              const s = String(v ?? "").trim();
              if (!s) return false;
              const arabic = (s.match(/[\u0600-\u06FF]/g) || []).length;
              return arabic >= 2; // any name with ≥2 Arabic letters
            };
            const ratioPhone = sample.filter((r) => looksPhone(r[iC])).length / sample.length;
            if (ratioPhone >= 0.5) {
              // Customer-header column is actually phones. Use it for phone, and look
              // left for the real name column. Override any prior iPH from header
              // since Retm exports sometimes misalign header labels with data columns.
              iPH = iC;
              for (let k = iC - 1; k >= 0; k--) {
                const ratioName = sample.filter((r) => isArabicName(r[k])).length / sample.length;
                if (ratioName >= 0.5) { iC = k; break; }
              }
            } else if (iPH >= 0) {
              // Sanity check: if the phone-header column is mostly Arabic text (branch
              // name etc.), fall back to scanning all columns for the actual phone col.
              const phoneColLooksPhone = sample.filter((r) => looksPhone(r[iPH])).length / sample.length;
              if (phoneColLooksPhone < 0.3) {
                let bestCol = -1, bestRatio = 0;
                for (let k = 0; k < (hd.length || 20); k++) {
                  if (k === iC || k === iD || k === iT || k === iX) continue;
                  const r = sample.filter((row) => looksPhone(row[k])).length / sample.length;
                  if (r > bestRatio) { bestRatio = r; bestCol = k; }
                }
                if (bestRatio >= 0.5) iPH = bestCol;
                else iPH = -1; // no reliable phone column
              }
            }
          }
          const out = [];
          rows.forEach((row) => {
            const { d, r: rawv } = parseDate(row[iD]);
            if (!d || d < "2026-04-16") return;
            const cl = String(row[iC] || "").trim();
            // Normalize phone: keep digits only, then prepend "0" if the Saudi mobile is missing the leading 0.
            let phRaw = "";
            if (iPH >= 0) {
              const d2 = String(row[iPH] || "").replace(/\D/g, "");
              if (d2) phRaw = d2.length === 9 && d2.startsWith("5") ? ("0" + d2) : d2;
            }
            const tot = parseFloat(row[iT]) || 0;
            const disc = iX >= 0 ? parseFloat(row[iX]) || 0 : 0;
            const dcRaw = iDC >= 0 ? String(row[iDC] || "").trim() : "";
            // Normalize coupon code: uppercase + strip whitespace. Empty stays empty.
            const discount_code = dcRaw ? dcRaw.toUpperCase().replace(/\s+/g, "") : "";
            const h = getHour(rawv);
            const sh = getShift(h);
            const is59x = detect59x(cl);
            const is59 = cl.includes("59") && !is59x;
            out.push({ date: d, raw: rawv, hour: h, shift: sh, client: cl, phone: phRaw, total: tot, disc, discount_code, is59, is59x, isFree: tot === 0 && disc > 0, isPaid: tot > 0 });
          });
          if (!out.length) { showMsg("r", "⚠ لا بيانات من 16 أبريل+"); return; }
          setInv(out);
          persist("inv", out);
          const phoneCount = out.filter((r) => r.phone).length;
          const phonePct = ((phoneCount / out.length) * 100).toFixed(0);
          const hint = phoneCount > 0
            ? ` (${phoneCount.toLocaleString("en-US")} معها جوال · ${phonePct}%)`
            : " — لا يوجد عمود الجوال";
          showMsg("g", `✅ ${out.length.toLocaleString("en-US")} فاتورة — ${out[0].date} إلى ${out[out.length - 1].date}${hint}`);
        } else if (tp === "cust") {
          const iN = ci("الاسم"), iP = ci("الهاتف");
          const iV = ci("الزيارات") >= 0 ? ci("الزيارات") : ci("عدد");
          const out = rows.map((row) => {
            const nm = String(row[iN] || "").trim();
            const is59x = detect59x(nm);
            return {
              name: nm,
              phone: String(row[iP] || "").replace(".0", "").trim(),
              visits: parseInt(row[iV], 10) || 0,
              is59: nm.includes("59") && !is59x,
              is59x,
            };
          }).filter((c) => c.name && c.name !== "nan");
          setCst(out);
          persist("cust", out);
          const cnt59x = out.filter((c) => c.is59x).length;
          showMsg("g", `✅ ${out.length.toLocaleString("en-US")} عميل${cnt59x > 0 ? ` (منهم ${cnt59x} استنفذوا الأكواب — 59 X)` : ""}`);
        } else if (tp === "prod") {
          // ── Products report from RETM (مبيعات المنتجات) ──────────────
          // We capture *every* detail column so the dashboard can report on
          // returns, cancellations, customer counts, gross-vs-net, etc.
          // Column order in the source file:
          //  الصنف | المجموع مع الضريبة | المبيعات بدون ضريبة |
          //  صافي المبيعات مع الضريبة | صافي المبيعات | الضرائب | قيمة الخصم |
          //  عدد المبيعات | متوسط الفاتورة | عدد العملاء | الكمية الصافية |
          //  التكلفة | قيمة الإرجاع | عدد الإرجاعات | قيمة الإلغاء |
          //  عدد الإلغاءات | الربح التقريب | الرسوم
          // Normalize headers to strip RTL marks/double-spaces — the file
          // also sometimes splits "الربح التقريب" into two cells.
          const _norm = (s) => String(s || "")
            .replace(/[\u200E\u200F\u202A-\u202E\u2066-\u2069\u061C]/g, "")
            .replace(/\s+/g, " ").trim();
          const hdN = hd.map(_norm);
          // Exact-match lookup to avoid substring collisions
          // (e.g. "صافي المبيعات" matching "صافي المبيعات مع الضريبة").
          const cx = (target) => hdN.findIndex((h) => h === _norm(target));
          const iN = cx("الصنف");
          // Headline revenue = "صافي المبيعات مع الضريبة" (= what the
          // customer actually paid, after discount, with VAT) — this is
          // the value that matches the RETM dashboard's main "Revenue"
          // figure. Falls back through other revenue columns if missing.
          const iR = cx("صافي المبيعات مع الضريبة") >= 0 ? cx("صافي المبيعات مع الضريبة")
                  : cx("المجموع مع الضريبة") >= 0 ? cx("المجموع مع الضريبة")
                  : cx("المجموع") >= 0 ? cx("المجموع") : -1;
          const iGross = cx("المجموع مع الضريبة");
          const iSalesNoTax = cx("المبيعات بدون ضريبة");
          const iNet = cx("صافي المبيعات");
          const iTax = cx("الضرائب");
          const iDisc = cx("قيمة الخصم") >= 0 ? cx("قيمة الخصم") : cx("الخصم");
          const iSalesCount = cx("عدد المبيعات");
          const iAvgInv = cx("متوسط الفاتورة");
          const iCust = cx("عدد العملاء");
          const iQ = cx("الكمية الصافية") >= 0 ? cx("الكمية الصافية") : cx("الكمية");
          const iC = cx("التكلفة");
          const iRetV = cx("قيمة الإرجاع");
          const iRetN = cx("عدد الإرجاعات");
          const iCanV = cx("قيمة الإلغاء");
          const iCanN = cx("عدد الإلغاءات");
          // "الربح التقريب" may appear as a single header or split into two
          // cells ("الربح" + "التقريب"). The numeric value is in the cell
          // labelled "الربح", so we fall back to that exact match.
          const iP2 = cx("الربح التقريب") >= 0 ? cx("الربح التقريب")
                    : cx("الربح التقريبي") >= 0 ? cx("الربح التقريبي")
                    : cx("الربح");
          const iFees = cx("الرسوم");
          const out = rows.map((row) => ({
            name: String(row[iN] || "").trim(),
            // headline figures used by existing dashboards
            rev:    iR  >= 0 ? (parseFloat(row[iR])  || 0) : 0,
            qty:    iQ  >= 0 ? (parseFloat(row[iQ])  || 0) : 0,
            cost:   iC  >= 0 ? (parseFloat(row[iC])  || 0) : 0,
            profit: iP2 >= 0 ? (parseFloat(row[iP2]) || 0) : 0,
            disc:   iDisc >= 0 ? (parseFloat(row[iDisc]) || 0) : 0,
            // expanded RETM detail — preserved for richer analytics later
            gross_with_tax: iGross      >= 0 ? (parseFloat(row[iGross])      || 0) : 0,
            sales_no_tax:   iSalesNoTax >= 0 ? (parseFloat(row[iSalesNoTax]) || 0) : 0,
            net:            iNet        >= 0 ? (parseFloat(row[iNet])        || 0) : 0,
            tax:            iTax        >= 0 ? (parseFloat(row[iTax])        || 0) : 0,
            sales_count:    iSalesCount >= 0 ? (parseFloat(row[iSalesCount]) || 0) : 0,
            avg_invoice:    iAvgInv     >= 0 ? (parseFloat(row[iAvgInv])     || 0) : 0,
            customers:      iCust       >= 0 ? (parseFloat(row[iCust])       || 0) : 0,
            return_value:   iRetV       >= 0 ? (parseFloat(row[iRetV])       || 0) : 0,
            return_count:   iRetN       >= 0 ? (parseFloat(row[iRetN])       || 0) : 0,
            cancel_value:   iCanV       >= 0 ? (parseFloat(row[iCanV])       || 0) : 0,
            cancel_count:   iCanN       >= 0 ? (parseFloat(row[iCanN])       || 0) : 0,
            fees:           iFees       >= 0 ? (parseFloat(row[iFees])       || 0) : 0,
          })).filter((p) => p.name && p.name.length > 1 && (p.rev > 0 || p.qty > 0));
          setProds(out);
          persist("prod", out);
          showMsg("g", `✅ ${out.length} منتج محمّل`);
        } else if (tp === "stk" || tp === "wst") {
          // Stock or waste report — same column layout (Retm-generated)
          const iId = ci("المعرف") >= 0 ? ci("المعرف") : ci("رمز المنتج");
          const iCode = ci("رمز المنتج");
          const iName = ci("الاسم");
          const iSU = ci("وحدة التخزين");
          const iCU = ci("وحدة الاستهلاك");
          const iMul = ci("المضاعف");
          const iQty = ci("إجمالي الكمية");
          const iPlan = ci("إجمالي الكمية المخطط");
          const iRsv = ci("إجمالي الكمية المحجوزة");
          if (iName < 0 || iQty < 0) {
            showMsg("r", "⚠ تأكد من وجود: الاسم وإجمالي الكمية");
            return;
          }
          const out = rows.map((row) => ({
            id: String(row[iId] || row[iCode] || "").trim(),
            code: String(row[iCode] || "").trim(),
            name: String(row[iName] || "").trim(),
            storage_unit: iSU >= 0 ? String(row[iSU] || "").trim() : "",
            consumption_unit: iCU >= 0 ? String(row[iCU] || "").trim() : "",
            multiplier: iMul >= 0 ? (parseFloat(row[iMul]) || 1) : 1,
            qty: parseFloat(row[iQty]) || 0,
            qty_planned: iPlan >= 0 ? (parseFloat(row[iPlan]) || 0) : 0,
            qty_reserved: iRsv >= 0 ? (parseFloat(row[iRsv]) || 0) : 0,
          })).filter((p) => p.name && p.name.length > 0);
          if (tp === "stk") {
            setStk(out);
            persist("stk", out);
            showMsg("g", `✅ ${out.length} صنف في المخزون`);
          } else {
            setWst(out);
            persist("wst", out);
            const totalWaste = out.reduce((s, r) => s + (r.qty || 0), 0);
            showMsg("g", `✅ ${out.length} صنف — إجمالي التالف: ${totalWaste.toLocaleString("en-US")}`);
          }
        } else if (tp === "cost") {
          // Cost-per-product file. Also handles the Retm "قائمة المنتجات" catalog
          // (columns: المعرف | رمز المنتج | الاسم | السعر | التكلفة | الحالة) — when present
          // we capture the selling price and the active flag too.
          const iName = ci("اسم المنتج") >= 0 ? ci("اسم المنتج") : ci("الاسم");
          const iCost = ci("التكلفة") >= 0 ? ci("التكلفة") : (ci("تكلفة") >= 0 ? ci("تكلفة") : ci("سعر التكلفة"));
          const iPrice = ci("السعر") >= 0 ? ci("السعر") : ci("سعر البيع");
          const iCat = ci("الفئة") >= 0 ? ci("الفئة") : ci("المجموعة");
          const iNote = ci("ملاحظة");
          const iStatus = ci("الحالة");
          if (iName < 0 || iCost < 0) {
            showMsg("r", "⚠ الملف يحتاج عمودين على الأقل: اسم المنتج + التكلفة");
            return;
          }
          const out = rows.map((row) => {
            const statusVal = iStatus >= 0 ? String(row[iStatus] || "").trim() : "";
            const isActive = statusVal === "" || statusVal === "✅" || statusVal === "نشط";
            return {
              name: String(row[iName] || "").trim(),
              cost: parseFloat(row[iCost]) || 0,
              price: iPrice >= 0 ? (parseFloat(row[iPrice]) || 0) : 0,
              category: iCat >= 0 ? String(row[iCat] || "").trim() : "",
              note: iNote >= 0 ? String(row[iNote] || "").trim() : "",
              active: isActive,
            };
          }).filter((r) => r.name && r.cost > 0);
          if (!out.length) { showMsg("r", "⚠ لا توجد منتجات صالحة"); return; }
          setCosts(out);
          persist("cost", out);
          const avg = out.reduce((s, r) => s + r.cost, 0) / out.length;
          const activeN = out.filter((r) => r.active).length;
          showMsg("g", `✅ ${out.length} منتج بتكلفة (${activeN} نشط) — متوسط تكلفة ${avg.toFixed(2)} ر`);
        } else if (tp === "purch") {
          // Purchases report from Retm.
          // Columns: المرجع | اسم المورد | الرقم الضريبى للمورد | رقم الفاتورة |
          //          الى تاريخ الفاتورة | تاريخ الانشاء | القيمة قبل الضريبة | الضريبة | القيمة شامل الضريبة
          // Notes:
          //  - "لم تنتهي" appears in numeric cells for draft invoices → treat as 0 and skip from totals.
          //  - Last row is "الاجمالي" — must be skipped.
          const iRef = ci("المرجع");
          const iSup = ci("اسم المورد") >= 0 ? ci("اسم المورد") : ci("المورد");
          const iTax = ci("الرقم الضريبى") >= 0 ? ci("الرقم الضريبى") : ci("الرقم الضريبي");
          const iNum = ci("رقم الفاتورة");
          const iDate = ci("تاريخ الفاتورة") >= 0 ? ci("تاريخ الفاتورة") : ci("تاريخ الانشاء");
          const iCreated = ci("تاريخ الانشاء");
          const iPre = ci("قبل الضريبة");
          const iVat = ci("الضريبة");
          const iTot = ci("شامل الضريبة") >= 0 ? ci("شامل الضريبة") : ci("الإجمالي");
          if (iSup < 0 || iDate < 0 || iTot < 0) {
            showMsg("r", "⚠ تأكد من وجود: اسم المورد، تاريخ الفاتورة، القيمة شامل الضريبة");
            return;
          }
          const parseNum = (v) => {
            if (v === null || v === undefined) return null;
            const s = String(v).trim();
            if (!s || s === "لم تنتهي" || s === "-") return null;
            const n = parseFloat(s);
            return Number.isFinite(n) ? n : null;
          };
          const out = [];
          rows.forEach((row) => {
            const supplier = String(row[iSup] || "").trim();
            // Skip "الاجمالي" summary row and empty rows
            if (!supplier || supplier === "الاجمالي" || supplier === "-") return;
            const ref = String(row[iRef] || "").trim();
            if (ref === "الاجمالي") return;
            const dateRaw = String(row[iDate] || "").trim();
            const { d } = parseDate(dateRaw);
            const pre = parseNum(row[iPre]);
            const vat = parseNum(row[iVat]);
            const tot = parseNum(row[iTot]);
            const draft = pre === null || tot === null;
            out.push({
              ref,
              supplier,
              tax_id: iTax >= 0 ? String(row[iTax] || "").trim() : "",
              invoice_num: iNum >= 0 ? String(row[iNum] || "").trim() : "",
              date: d || "",
              raw_date: dateRaw,
              created_at: iCreated >= 0 ? String(row[iCreated] || "").trim() : "",
              pre_tax: pre || 0,
              tax: vat || 0,
              total: tot || 0,
              draft,
            });
          });
          if (!out.length) { showMsg("r", "⚠ لا توجد فواتير صالحة في الملف"); return; }
          setPurch(out);
          persist("purch", out);
          const closed = out.filter((p) => !p.draft);
          const totalAll = closed.reduce((s, p) => s + p.total, 0);
          const draftCount = out.length - closed.length;
          showMsg(
            "g",
            `✅ ${out.length.toLocaleString("en-US")} فاتورة شراء — مجموع: ${totalAll.toLocaleString("en-US", { maximumFractionDigits: 2 })} ر${draftCount > 0 ? ` (منها ${draftCount} مسوّدة لم تنتهِ)` : ""}`,
          );
        }
      } catch (err) {
        console.error(err);
        showMsg("r", "⚠ خطأ: " + err.message);
      }
    };
    r.readAsArrayBuffer(file);
  };

  // v119.92 — Centralised Marketing Plan Excel upload.
  // Posts FormData to /api/marketing-plans/upload (Claude parses + analyses
  // the workbook server-side). After success we dispatch a window event so
  // the open MarketingPlansPanel can refresh its list without a page reload.
  const uploadMarketingPlan = useCallback(async (file) => {
    if (!file) return;
    if (!canUpload.mkt_plan) {
      showMsg("r", "ليس لديك صلاحية رفع الخطط التسويقية");
      return;
    }
    const name = (file.name || "").toLowerCase();
    if (!name.endsWith(".xlsx") && !name.endsWith(".xlsm")) {
      showMsg("r", "⚠ يُقبل فقط .xlsx أو .xlsm للخطط التسويقية");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      showMsg("r", "⚠ حجم ملف الخطة أكبر من 10MB");
      return;
    }
    showMsg("g", "⏳ جارٍ تحليل الخطة عبر Claude…", 0);
    try {
      const fd = new FormData();
      fd.append("file", file, file.name);
      const { data } = await api.post("/marketing-plans/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const p = data?.plan || {};
      const overlap = (data?.overlap_with_details || []).length;
      const msg = overlap > 0
        ? `⚠ تم رفع الخطة (${p.period_label || "—"}) — تداخل مع ${overlap} خطة سابقة. راجعها من «التسويق ← الخطط التسويقية».`
        : `✅ تم رفع الخطة (${p.period_label || "—"}) — جاهزة للاعتماد من «التسويق ← الخطط التسويقية».`;
      showMsg("g", msg, 9000);
      // Notify any open MarketingPlansPanel to reload its list
      window.dispatchEvent(new CustomEvent("tw-marketing-plan-uploaded", { detail: { plan_id: p.id } }));
    } catch (e) {
      showMsg("r", e?.response?.data?.detail || "⚠ فشل رفع الخطة التسويقية");
    }
  }, [canUpload.mkt_plan]);

  const exportJSON = () => {
    const payload = { inv, cst, prods, ts: new Date().toISOString() };
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([JSON.stringify(payload)], { type: "application/json" }));
    a.download = "thawany_" + new Date().toISOString().slice(0, 10) + ".json";
    a.click();
    setStMsg({ t: "g", m: "✅ تم التصدير" });
    setTimeout(() => setStMsg(null), 4000);
  };

  const importJSON = (file) => {
    if (!file) return;
    const r = new FileReader();
    r.onload = async (e) => {
      try {
        const d = JSON.parse(e.target.result);
        if (d.inv && canUpload.inv) { setInv(d.inv); await persist("inv", d.inv); }
        if (d.cst && canUpload.cust) { setCst(d.cst); await persist("cust", d.cst); }
        if (d.prods && canUpload.prod) { setProds(d.prods); await persist("prod", d.prods); }
        showMsg("g", "✅ تم الاستيراد");
      } catch (err) { showMsg("r", "⚠ خطأ في الملف"); }
    };
    r.readAsText(file);
  };

  const clearAll = async () => {
    if (user.role !== "admin") return;
    if (!window.confirm("مسح كل البيانات؟ هذه العملية لا يمكن التراجع عنها.")) return;
    try {
      await api.delete("/data/all");
      setInv([]); setCst([]); setProds([]);
      showMsg("g", "✅ تم مسح كل البيانات");
    } catch (e) { showMsg("r", formatErr(e.response?.data?.detail)); }
  };

  const headerDate = useMemo(
    () => formatKSADate(new Date(), { weekday: "short", month: "long", day: "numeric" }),
    []
  );
  const isEmpty = inv.length === 0 && stk.length === 0 && wst.length === 0;
  const roleBadge = roleBadgeFor(user.role);

  // Determine live status for the header badge
  const latestUpdated = useMemo(() => {
    const ts = ["inv", "cust", "prod"]
      .map((k) => meta?.[k]?.updated_at)
      .filter(Boolean)
      .sort();
    return ts[ts.length - 1] || null;
  }, [meta]);
  const liveSource = useMemo(() => {
    const sources = ["inv", "cust", "prod"].map((k) => meta?.[k]?.source).filter(Boolean);
    return sources.includes("retm") ? "retm" : sources[0] || null;
  }, [meta]);
  const liveStatus = useMemo(() => {
    if (!latestUpdated) return "off";
    const ageMin = (Date.now() - new Date(latestUpdated).getTime()) / 60000;
    if (liveSource === "retm" && ageMin < 15) return "live";
    if (ageMin < 60 * 24) return "stale";
    return "stale";
  }, [latestUpdated, liveSource]);

  // Auto-correct active tab if user has no access (e.g. role swap).
  // v119.142 — Wait until roles load so we preserve URL-driven initial tab.
  useEffect(() => {
    if (!tabsForRole.length) return;  // still loading
    if (!tabsForRole.find((t) => t.id === tab) && tab !== "tpl_demo") {
      setTab(tabsForRole[0].id);
    }
  }, [tabsForRole, tab, setTab]);

  return (
    <>
      <div className="hdr">
        <div className="hdr-left">
          <div>
            <img
              src="/thawany-logo.png"
              alt="ثواني — Thawany Smart Dashboard"
              className="hdr-logo-img"
              data-testid="hdr-logo"
            />
          </div>
        </div>
        <div className="hdr-btns">
          <GlobalSearch onJump={(typeOrTab) => {
            // typeOrTab may be a tab id (from AI answer.deep_link) or a result-list type
            const map = { finance: "fin", mystery: "mystery", customers: "cmp", products: "ops", campaigns: "cmp", employees: "st" };
            // Accept both: if already a tab id, use it as-is; otherwise translate
            const knownTabs = ["home", "fin", "mystery", "cmp", "ops", "mkt", "anl", "st", "branch", "stk", "adm", "log"];
            const target = knownTabs.includes(typeOrTab) ? typeOrTab : map[typeOrTab];
            if (target) setTab(target);
          }} />
          {/* v119.83 — WhatsApp quick shortcut: jumps the user straight to
              the WhatsApp panel from anywhere in the dashboard.
              v148 — الفحص بالصلاحيات (وليس الأدوار الثابتة) حتى تظهر لأصحاب
              الأدوار المخصصة مثل «أخصائية التسويق» الحاصلين على whatsapp.send/chat.
              «إدارة أرقام المدراء» تبقى معزولة داخل اللوحة بصلاحية whatsapp.admin_phones. */}
          {(user.role === "admin" || user.role === "ceo"
            || hasPerm("whatsapp.send") || hasPerm("whatsapp.chat") || hasPerm("whatsapp.full")) && (
            <button
              type="button"
              onClick={() => setTab("whatsapp")}
              data-testid="hdr-whatsapp-shortcut"
              title="فتح لوحة WhatsApp"
              style={{
                background: "linear-gradient(135deg, #25d366 0%, #128c7e 100%)",
                color: "#fff",
                border: "none",
                width: 34,
                height: 34,
                borderRadius: "50%",
                fontSize: 16,
                lineHeight: 1,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                boxShadow: "0 2px 6px rgba(37, 211, 102, 0.35)",
              }}
            >
              💬
            </button>
          )}
          {/* v200.29 · Environment indicator moved from the deleted
              EnvBanner into the header cluster. Polarity is enforced:
              production = quiet green dot; non-production = amber pill
              with env name always visible. */}
          <EnvDot compact />
          {/* v119.151 — Notification Bell (in-app center + badge counts) */}
          <NotificationBell onNav={(tabId) => setTab(tabId)} />
          {/* v119.144 — Global PeriodPicker removed. Each page owns its date filter. */}
          <div data-testid="header-date" className="hdr-date">{headerDate}</div>
          <HeaderUserMenu
            user={user}
            role={{ ...roleBadge, key: user.role || "viewer" }}
            theme={theme}
            onToggleTheme={toggleTheme}
            onLogout={logout}
            onNav={setTab}
            isAdmin={user.role === "admin"}
            canSettings={tabsForRole.some((t) => t.id === "st")}
          />
        </div>
      </div>

      {/* Dedicated upload bar — visually separated from navigation tabs */}
      {/* Upload buttons moved to Settings tab → UploadPanel for cleaner navigation */}
      {/* Hidden file inputs (refs used both by UploadPanel and by empty-state shortcuts in tabs) */}
      <input data-testid="input-invoices" type="file" ref={fiRef} accept=".xlsx,.xls" style={{ display: "none" }} onChange={(e) => { loadXL(e.target.files?.[0], "inv"); e.target.value = ""; }} />
      <input data-testid="input-customers" type="file" ref={fcRef} accept=".xlsx,.xls" style={{ display: "none" }} onChange={(e) => { loadXL(e.target.files?.[0], "cust"); e.target.value = ""; }} />
      <input data-testid="input-products" type="file" ref={fpRef} accept=".xlsx,.xls" style={{ display: "none" }} onChange={(e) => { loadXL(e.target.files?.[0], "prod"); e.target.value = ""; }} />
      <input data-testid="input-stock" type="file" ref={fsRef} accept=".xlsx,.xls" style={{ display: "none" }} onChange={(e) => { loadXL(e.target.files?.[0], "stk"); e.target.value = ""; }} />
      <input data-testid="input-waste" type="file" ref={fwRef} accept=".xlsx,.xls" style={{ display: "none" }} onChange={(e) => { loadXL(e.target.files?.[0], "wst"); e.target.value = ""; }} />
      <input data-testid="input-cost" type="file" ref={fkRef} accept=".xlsx,.xls" style={{ display: "none" }} onChange={(e) => { loadXL(e.target.files?.[0], "cost"); e.target.value = ""; }} />
      <input data-testid="input-purch" type="file" ref={fpurRef} accept=".xlsx,.xls" style={{ display: "none" }} onChange={(e) => { loadXL(e.target.files?.[0], "purch"); e.target.value = ""; }} />
      <input data-testid="input-mkt" type="file" ref={fmktRef} accept=".xlsx,.xls" style={{ display: "none" }} onChange={(e) => { loadXL(e.target.files?.[0], "mkt"); e.target.value = ""; }} />
      <input data-testid="input-mkt-plan" type="file" ref={fmktPlanRef} accept=".xlsx,.xlsm" style={{ display: "none" }} onChange={(e) => { uploadMarketingPlan(e.target.files?.[0]); e.target.value = ""; }} />
      <input data-testid="input-dsum" type="file" ref={fdsRef} accept=".xlsx,.xls" style={{ display: "none" }} onChange={(e) => { loadXL(e.target.files?.[0], "dsum"); e.target.value = ""; }} />

      <input data-testid="input-json" type="file" ref={fjRef} accept=".json" onChange={(e) => { importJSON(e.target.files?.[0]); e.target.value = ""; }} />

      <GroupedTabs
        tab={tab}
        setTab={setTab}
        tabsForRole={tabsForRole}
        byId={Object.fromEntries(ALL_TABS.map((t) => [t.id, t]))}
        badges={{ reqs: pendingReqsCount }}
      />

      <MobileNavDrawer
        tab={tab}
        setTab={setTab}
        tabsForRole={tabsForRole}
        byId={Object.fromEntries(ALL_TABS.map((t) => [t.id, t]))}
        badges={{ reqs: pendingReqsCount }}
      />

      <div className="main" key={tab}>
        <PanelErrorBoundary>
        <div className="tw-page-enter">
        <Alert msg={msg} />

        {/* HomePage paints its own data from /api/ai/personal-home and does
            not depend on /data/all. Render it immediately — never block on
            the bulk dataset load, which is the #1 cause of slow PWA opens
            (the user previously saw a multi-second blank "جارٍ التحميل…"
            screen on every cold launch). */}
        {tab === "home" && (
          <HomePage
            onNavigate={(t) => setTab(t)}
            renderRoleDash={(r) => {
              switch (r) {
                case "ceo":            return <CEOPage />;
                case "branch_manager": return <BranchManager fil={fil} dsum={dsum} prods={prods} />;
                case "operations":     return <OperationsManager fil={fil} dsum={dsum} prods={prods} dateFrom={dateFrom} dateTo={dateTo} />;
                case "marketing":      return <MarketingManager />;
                case "advisor":
                case "consultant":     return <ConsultantPage />;
                case "layla_inst":     return <LaylaInstitutePage />;
                default:               return null;
              }
            }}
          />
        )}

        {tab !== "home" && !loaded && <div className="card" style={{ textAlign: "center", padding: 30 }}>⏳ جارٍ التحميل...</div>}

        {loaded && isEmpty && tab !== "st" && tab !== "audit" && tab !== "mkc" && tab !== "camps" && tab !== "events" && tab !== "meet" && tab !== "errs" && tab !== "home" && tab !== "quality"
          && tab !== "reqs" && tab !== "training" && tab !== "hr_academy" && tab !== "employees" && tab !== "ceo" && tab !== "mk_plans" && tab !== "mk_gantt" && tab !== "mk_manager" && tab !== "consultant" && tab !== "layla_inst" && tab !== "notif_rules" && tab !== "camp_sched" && tab !== "mystery"
          && tab !== "surveys" && tab !== "hr_surveys" && tab !== "social" && tab !== "my_work" && tab !== "damage_mgmt" && tab !== "my_shift_tasks" && tab !== "shift_tasks_mgmt" && tab !== "shift_close" && tab !== "shift_closures_log" && tab !== "cash_deficit" && tab !== "freshness" && tab !== "make" && tab !== "whatsapp"
          && tab !== "amb" && tab !== "sys_log" && tab !== "events_cal" && tab !== "op_manager" && tab !== "pos" && tab !== "retm_cust" && tab !== "expected" && tab !== "lab" && (
          <div data-testid="landing-section">
            <div className="welcome-banner welcome-banner-compact" data-testid="welcome-banner">
              <div className="welcome-emoji" aria-hidden>{roleBadge.lbl.split(" ")[0]}</div>
              <div className="welcome-body">
                <div className="welcome-title" data-testid="welcome-title">
                  مرحباً، {user.name || user.username}
                  {welcomeText && (
                    <span className="welcome-sub-inline" data-testid="welcome-sub">
                      {" · "}{welcomeText}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="g2" style={{ marginTop: 16 }}>
              {canUpload.inv && (
                <div className="uzone" onClick={() => fiRef.current?.click()} data-testid="uzone-inv">
                  <div className="ui">📊</div>
                  <h3>تقرير الفواتير</h3>
                  <p>تقرير_الفواتير.xlsx من رتم</p>
                </div>
              )}
              {canUpload.cust && (
                <div className="uzone" onClick={() => fcRef.current?.click()} data-testid="uzone-cust">
                  <div className="ui">👥</div>
                  <h3>ملف العملاء</h3>
                  <p>العملاء.xlsx من رتم</p>
                </div>
              )}
              {canUpload.prod && (
                <div className="uzone" onClick={() => fpRef.current?.click()} data-testid="uzone-prod">
                  <div className="ui">📦</div>
                  <h3>تقرير المنتجات</h3>
                  <p>تقرير الأصناف.xlsx من رتم</p>
                </div>
              )}
              {(canUpload.inv || canUpload.cust || canUpload.prod) && (
                <div className="uzone" onClick={() => fjRef.current?.click()} data-testid="uzone-json">
                  <div className="ui">📥</div>
                  <h3>استيراد بيانات محفوظة</h3>
                  <p>ملف JSON من جهاز آخر</p>
                </div>
              )}
              {user.role === "viewer" && (
                <div className="card" data-testid="viewer-empty-hint" style={{ gridColumn: "1 / -1", textAlign: "center", padding: 30, background: "var(--beige)" }}>
                  <div style={{ fontSize: 36, marginBottom: 8 }}>👁</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "var(--brown)", marginBottom: 6 }}>بانتظار رفع البيانات من المدير</div>
                  <p style={{ fontSize: 12, color: "var(--muted)", lineHeight: 1.7, marginTop: 8 }}>
                    حسابك للاستعراض فقط — لا يمكنك رفع ملفات. حالما يرفع المدير تقارير رتم ستظهر التحليلات والقوائم تلقائياً.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {loaded && (!isEmpty || tab === "ai" || tab === "adv" || tab === "marketing_hub" || tab === "comms_hub" || tab === "st" || tab === "manual" || tab === "mk" || tab === "mkc" || tab === "mk_plans" || tab === "mk_gantt" || tab === "amb" || tab === "audit" || tab === "sys_log" || tab === "events_cal" || tab === "camps" || tab === "impact" || tab === "social" || tab === "events" || tab === "meet" || tab === "errs" || tab === "mystery" || tab === "branch" || tab === "fin" || tab === "purch" || tab === "marg" || tab === "op_manager" || tab === "mk_manager" || tab === "consultant" || tab === "layla_inst" || tab === "notif_rules" || tab === "camp_sched" || tab === "employees" || tab === "quality" || tab === "ceo" || tab === "training" || tab === "hr_academy" || tab === "reqs" || tab === "surveys" || tab === "hr_surveys" || tab === "my_work" || tab === "damage_mgmt" || tab === "my_shift_tasks" || tab === "shift_tasks_mgmt" || tab === "shift_close" || tab === "shift_closures_log" || tab === "cash_deficit" || tab === "freshness" || tab === "make" || tab === "whatsapp" || tab === "pos" || tab === "retm_cust" || tab === "expected" || tab === "lab" || tab === "customer_hub" || tab === "system_diagnostics") && tab !== "home" && (() => {
          return (
          <div data-testid="dashboard-section">

            {tab === "ai" && (
              <>
                <AIInsights />
                <div style={{ height: 16 }} />
                <Advanced />
              </>
            )}
            {tab === "adv" && <Advanced />}
            {tab === "sim" && <Simulator fil={fil} prods={prods} costs={costs} />}
            {tab === "sales" && <SalesAndOps fil={fil} cst={cst} dsum={dsum} period={period} dateFrom={dateFrom} dateTo={dateTo} />}
            {tab === "pos" && <Suspense fallback={<div className="corner-loader" aria-label="جارٍ التحميل" />}><POSPage /></Suspense>}
            {tab === "retm_cust" && <Suspense fallback={<div className="corner-loader" aria-label="جارٍ التحميل" />}><POSCustomers /></Suspense>}
            {tab === "cu" && (() => {
              // v200.48 (2026-08-13) — «شرائح العملاء» merged into
              // «مركز العملاء الموحد» → tab «directory». Old tab kept
              // as a redirect so bookmarked links don't 404. Preserves
              // rule 6 (never delete a working surface).
              try {
                localStorage.setItem("hub.default_sub_tab", "segments");
              } catch (_e) { /* storage disabled */ }
              // Schedule a redirect on next tick; render an inline
              // notice card meanwhile (also serves as a data-testid
              // anchor for the redirect guard).
              setTimeout(() => { try { setTab("customer_hub"); } catch (_e) { /* noop */ } }, 400);
              return (
                <div data-testid="cu-redirect-notice" style={{
                  maxWidth: 640, margin: "40px auto", padding: 20,
                  border: "1px solid rgba(170,149,102,0.35)",
                  background: "rgba(170,149,102,0.10)",
                  borderRadius: 12, textAlign: "center",
                  color: "#AA9566", fontFamily: "'El Messiri', sans-serif",
                }}>
                  <div style={{ fontSize: 24, marginBottom: 6 }}>🎯</div>
                  <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>
                    «شرائح العملاء» انتقلت إلى «مركز العملاء الموحد»
                  </div>
                  <div style={{ fontSize: 12, color: "#78716C" }}>
                    جارٍ التحويل إلى تبويب «دليل العملاء»…
                  </div>
                  <button type="button" onClick={() => setTab("customer_hub")}
                    data-testid="cu-redirect-btn"
                    style={{
                      marginTop: 14, padding: "8px 18px",
                      border: "none", borderRadius: 8,
                      background: "#AA9566", color: "#fff",
                      fontWeight: 700, cursor: "pointer",
                    }}>
                    الانتقال الآن
                  </button>
                </div>
              );
            })()}
            {tab === "customer_hub" && <Suspense fallback={<div className="corner-loader" aria-label="جارٍ التحميل" />}><CustomerHub /></Suspense>}
            {tab === "cashier" && <CashierCard />}
            {tab === "expected" && <ExpectedToday />}
            {tab === "lab" && <CustomerLab />}
            {tab === "pr" && <Products prods={prods} onUpload={() => fpRef.current?.click()} user={user} dateFrom={dateFrom} dateTo={dateTo} period={period} />}
            {tab === "stk" && <Inventory stk={stk} onUpload={() => fsRef.current?.click()} />}
            {tab === "wst" && <Waste wst={wst} stk={stk} onUpload={() => fwRef.current?.click()} />}
            {tab === "purch" && <Purchases purch={purch} canUpload={canUpload.purch} onUpload={() => fpurRef.current?.click()} />}
            {tab === "marg" && <Margins prods={prods} costs={costs} purch={purch} canUpload={canUpload.prod || canUpload.cost} onUploadProds={() => fpRef.current?.click()} onUploadCosts={() => fkRef.current?.click()} />}
            {tab === "mk" && <Marketing />}
            {tab === "marketing_hub" && <MarketingHub />}
            {tab === "system_diagnostics" && (
              <Suspense fallback={<div className="corner-loader" aria-label="جارٍ التحميل" />}>
                <SystemDiagnostics />
              </Suspense>
            )}
            {tab === "comms_hub" && <ChannelsHub user={user} hasPerm={hasPerm} />}
            {tab === "mkc" && <MarketingConsultant />}
            {tab === "mk_plans" && <MarketingPlansPanel />}
            {tab === "mk_gantt" && <MarketingGanttPage />}
            {tab === "amb" && (
              <Suspense fallback={<div className="corner-loader" aria-label="جارٍ التحميل" />}>
                <AmbassadorsPanel />
              </Suspense>
            )}
            {tab === "camps" && <Campaigns />}
            {tab === "tpl_demo" && (
              <Suspense fallback={<div className="corner-loader" aria-label="جارٍ التحميل" />}>
                <TemplatePickerDemo />
              </Suspense>
            )}
            {tab === "impact" && <CampaignImpact />}
            {tab === "social" && <SocialMedia />}
            {tab === "events" && <Events />}
            {tab === "meet" && <Meetings />}
            {tab === "errs" && <ErrorsLearning />}
            {(tab === "mystery" || tab === "branch" || tab === "fin" || tab === "op_manager" || tab === "mk_manager" || tab === "consultant" || tab === "layla_inst" || tab === "notif_rules" || tab === "camp_sched" || tab === "employees" || tab === "quality" || tab === "ceo" || tab === "training" || tab === "hr_academy" || tab === "reqs" || tab === "surveys" || tab === "hr_surveys") && (
              <Suspense fallback={<div className="corner-loader" aria-label="جارٍ التحميل" />}>
                {tab === "mystery" && <MysteryShopper />}
                {tab === "branch" && <BranchManager fil={fil} dsum={dsum} prods={prods} />}
                {tab === "fin" && <Finance />}
                {tab === "op_manager" && <OperationsManager fil={fil} dsum={dsum} prods={prods} dateFrom={dateFrom} dateTo={dateTo} />}
                {tab === "mk_manager" && <MarketingManager />}
                {tab === "consultant" && <ConsultantPage />}
                {tab === "layla_inst" && <LaylaInstitutePage />}
                {tab === "notif_rules" && <NotificationRulesUI />}
                {tab === "camp_sched" && (() => {
                  // v-phase3 2026-06 — «الحملات المجدولة» نُقلت مرجعيًا إلى
                  // مركز التسويق ← الحملات. نبقي المسار حيًّا كإعادة توجيه.
                  setTimeout(() => { try { const u = new URL(window.location.href); u.searchParams.set("sub", "camp_sched"); window.history.replaceState(null, "", u.toString()); setTab("marketing_hub"); } catch (_e) { /* noop */ } }, 300);
                  return (
                    <div data-testid="camp-sched-redirect-notice" style={{ padding: 24, textAlign: "center", color: "var(--brown-deep)", fontSize: 13, lineHeight: 1.9 }}>
                      ⏳ «الحملات المجدولة» انتقلت إلى «مركز التسويق ← الحملات». يجري تحويلك...
                      <div>
                        <button type="button" data-testid="camp-sched-redirect-btn" className="btn btn-dark" style={{ marginTop: 10, fontSize: 12 }}
                          onClick={() => { const u = new URL(window.location.href); u.searchParams.set("sub", "camp_sched"); window.history.replaceState(null, "", u.toString()); setTab("marketing_hub"); }}>
                          افتح الآن
                        </button>
                      </div>
                    </div>
                  );
                })()}
                {tab === "employees" && <Employees />}
                {tab === "quality" && <QualityReports />}
                {tab === "ceo" && <CEOPage />}
                {tab === "training" && <TrainingPage />}
                {tab === "hr_academy" && <HRAcademyManagement />}
                {tab === "reqs" && <RequestsPage />}
                {tab === "surveys" && (
                  <CustomerSurveys customers={(() => {
                    const fromFile = buildFromCustomerFile(cst, fil);
                    if (fromFile.length > 0) return fromFile;
                    return buildCustomersFromInvoices(fil);
                  })()} />
                )}
                {tab === "hr_surveys" && <HRSurveys />}
              </Suspense>
            )}
            {tab === "audit" && (
              <>
                {user.role === "admin" && (
                  <div style={{ marginBottom: 16 }}>
                    <RbacAuditPanel />
                  </div>
                )}
                <AuditLog />
                {/* v119.197 — سجل تحسين النظام مدموج هنا (سجل واحد) */}
                {user.role === "admin" && (
                  <div style={{ marginTop: 16 }}>
                    <SystemOptimizationPanel />
                  </div>
                )}
              </>
            )}
            {/* ceo_meetings tab removed (v119.11) — moved as sub-section inside CEOPage */}
            {tab === "my_work" && <MyWorkPanel />}
            {tab === "damage_mgmt" && <DamageManagementPanel user={user} />}
            {tab === "my_shift_tasks" && <MyShiftTasks />}
            {tab === "shift_tasks_mgmt" && <BranchManagerTasksPanel />}
            {tab === "shift_close" && <ShiftClose />}
            {tab === "shift_closures_log" && <ShiftClosuresLog />}
            {tab === "cash_deficit" && <CashDeficitPanel />}
            {tab === "freshness" && <FreshnessPanel />}
            {tab === "make" && <MakeIntegrationPage user={user} hasPerm={hasPerm} />}
            {tab === "whatsapp" && (user.role === "admin" || user.role === "marketing" || hasPerm("whatsapp.send") || hasPerm("whatsapp.chat") || hasPerm("whatsapp.full")) && <WhatsAppPanel />}
            {tab === "sys_log" && (user.role === "admin" || hasPerm("system.audit")) && <SystemOptimizationPanel />}
            {tab === "manual" && <ThawanyManual />}
            {tab === "events_cal" && (user.role === "admin" || hasPerm("system.audit")) && <EventsCalendarPanel />}
            {tab === "st" && (
              <SettingsCatProvider>
                {/* v119.209 — iOS Settings tabs (Executive redesign) */}
                <SettingsTabsBar />
                <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 8, padding: "0 4px", lineHeight: 1.7 }}>
                  💡 الإعدادات منظمة بتبويبات iOS — اختر الفئة ثم افتح القسم المطلوب.
                </div>

                {/* ─── القسم 1: رفع الملفات ─── */}
                <CollapsibleSection
                  id="files"
                  icon="📂"
                  title="رفع الملفات"
                  subtitle="فواتير · عملاء · منتجات · تقارير مالية وسرّية · موظفين · كوبونات شركاء"
                  defaultOpen={false}
                >
                  {(canUpload.inv || canUpload.cust || canUpload.prod || canUpload.stk || canUpload.wst || canUpload.cost || canUpload.purch || canUpload.mkt || canUpload.dsum || canUpload.mkt_plan) && (
                    <UploadPanel
                      canUpload={canUpload}
                      fileRefs={{ inv: fiRef, cust: fcRef, prod: fpRef, stk: fsRef, wst: fwRef, cost: fkRef, purch: fpurRef, mkt: fmktRef, dsum: fdsRef, mkt_plan: fmktPlanRef }}
                      onPickFile={(t) => {
                        const refMap = { inv: fiRef, cust: fcRef, prod: fpRef, stk: fsRef, wst: fwRef, cost: fkRef, purch: fpurRef, mkt: fmktRef, dsum: fdsRef, mkt_plan: fmktPlanRef };
                        refMap[t]?.current?.click();
                      }}
                      onFileDrop={(t, file) => {
                        if (t === "mkt_plan") { uploadMarketingPlan(file); return; }
                        loadXL(file, t);
                      }}
                      onDownloadTemplate={downloadXlTemplate}
                      meta={meta}
                    />
                  )}
                  {(user.role === "admin" || user.role === "marketing") && (
                    <Suspense fallback={null}>
                      <AmbassadorCouponsCard />
                    </Suspense>
                  )}
                  {hasPerm("settings.uploads") && (
                    <ReportsUploadPanel isAdmin={user.role === "admin"} />
                  )}
                  {user.role === "admin" && (
                    <Suspense fallback={<div style={{ padding: 14, fontSize: 12, color: "var(--muted)" }}>...</div>}>
                      <UploadPortalsPanel />
                    </Suspense>
                  )}
                </CollapsibleSection>

                {/* ─── v119.97 — هوية الشركة والفروع (Single source of truth) ─── */}
                {hasPerm("settings.company_identity") && (
                  <CollapsibleSection
                    id="company-identity"
                    icon="🏢"
                    title="هوية الشركة والفروع"
                    subtitle="اسم المساعد · الرسالة · الرؤية · الفروع · العروض · تصحيح الأسعار — يُدخل مرّة واحدة ويُقرأ من كل مكان"
                    defaultOpen={false}
                  >
                    <Suspense fallback={<div style={{ padding: 14 }}>⏳ تحميل...</div>}>
                      <SettingsCompanyIdentity />
                    </Suspense>
                  </CollapsibleSection>
                )}

                {/* ─── طلب مالك — الحد الأدنى لعمر المراسلة التسويقية (owner only) ─── */}
                {(user.role === "admin" || user.role === "ceo") && (
                  <CollapsibleSection
                    id="marketing-min-age"
                    icon="🔞"
                    title="الحد الأدنى لعمر المراسلة"
                    subtitle="يتحكم باستبعاد القاصرين من بانِي الجمهور — 0 = بلا استبعاد تلقائي"
                    defaultOpen={false}
                  >
                    <Suspense fallback={<div style={{ padding: 14 }}>⏳ تحميل...</div>}>
                      <MarketingMinAgeSetting />
                    </Suspense>
                  </CollapsibleSection>
                )}

                {/* ─── Iter42 — التصنيفات (أنواع التفاعل/المواضيع/الحلول/أنواع الحملات + مرادفات المنتجات) ─── */}
                {hasPerm("settings.company_identity") && (
                  <CollapsibleSection
                    id="categories"
                    icon="🏷️"
                    title="التصنيفات"
                    subtitle="أنواع التفاعل · المواضيع · الحلول · أنواع الحملات · مرادفات المنتجات — إضافة وإعادة تسمية وأرشفة بسبب إلزامي"
                    defaultOpen={false}
                  >
                    <Suspense fallback={<div style={{ padding: 14 }}>⏳ تحميل...</div>}>
                      <CategoriesSettings />
                    </Suspense>
                  </CollapsibleSection>
                )}

                {/* ─── v119.89 — مركز التكاملات الموحّد ─── */}
                {hasPerm("settings.integrations") && (
                  <CollapsibleSection
                    id="integrations-hub"
                    icon="🔌"
                    title="مركز التكاملات الخارجية"
                    subtitle="WhatsApp · Retm · Malhr · Make · Resend · Emergent LLM"
                    defaultOpen={false}
                  >
                    <Suspense fallback={<div style={{ padding: 14 }}>⏳ تحميل...</div>}>
                      <IntegrationsHubPanel />
                    </Suspense>
                  </CollapsibleSection>
                )}

                {/* ─── v119.237 — الذكاء الميداني والعمليات (admin/ceo/operations) ─── */}
                {(user.role === "admin" || user.role === "ceo" || user.role === "operations") && (
                  <CollapsibleSection
                    id="field-intel"
                    icon="🛰"
                    title="الذكاء الميداني والعمليات"
                    subtitle="كاميرا اللوحات (LPR) · كاميرا التعابير · شاشة الفرع · المواصفات الشرائية"
                    defaultOpen={false}
                  >
                    <Suspense fallback={<div style={{ padding: 14 }}>⏳ تحميل...</div>}>
                      <FieldIntelPanel />
                    </Suspense>
                  </CollapsibleSection>
                )}

                {/* ─── v119.205 — مستشار Emergent التقني (admin/ceo) ─── */}
                {(user.role === "admin" || user.role === "ceo") && (
                  <CollapsibleSection
                    id="tech-advisor"
                    icon="🧭"
                    title="مستشار Emergent التقني"
                    subtitle="صياغة الأوامر البرمجية · مراقبة صحة النظام · منع هدر الاعتمادات"
                    defaultOpen={false}
                  >
                    <Suspense fallback={<div style={{ padding: 14 }}>⏳ تحميل...</div>}>
                      <TechAdvisorPanel />
                    </Suspense>
                  </CollapsibleSection>
                )}

                {/* ─── v119.232 — لوحة حوكمة Emergent (Governance) ─── */}
                {(user.role === "admin" || user.role === "ceo") && (
                  <CollapsibleSection
                    id="emergent-governance"
                    icon="🛡️"
                    title="لوحة حوكمة Emergent"
                    subtitle="إحصائيات الكود اللحظية · صحة التكاملات · نشاط AI · رأي Claude الاستراتيجي"
                    defaultOpen={false}
                  >
                    <Suspense fallback={<div style={{ padding: 14 }}>⏳ تحميل...</div>}>
                      <EmergentAdvisorPanel />
                    </Suspense>
                  </CollapsibleSection>
                )}

                {/* ─── v119.164 — دليل النظام الشامل (PDF) ─── */}
                <CollapsibleSection
                  id="system-manual"
                  icon="📘"
                  title="دليل النظام الشامل (PDF)"
                  subtitle="شرح كامل لكل صفحة وميزة — للفريق والمستشار — قابل للطباعة أو الحفظ PDF"
                  defaultOpen={false}
                >
                  <div style={{ padding: 16 }} data-testid="system-manual-section">
                    <p style={{ fontSize: 13, lineHeight: 1.8, color: "#5c3a1e", margin: "0 0 12px" }}>
                      دليل شامل يشرح النظام بالكامل: كل صفحة، كل ميزة، طريقة الاستخدام، وخارطة الطريق للمستقبل.
                      مخصص للفريق (المستشار · التسويق · المبيعات · الموظفين). الأقسام الخاصة بالمدير التنفيذي تُخفى تلقائياً حسب الدور.
                    </p>
                    <p style={{ fontSize: 12, color: "#8b5a2b", margin: "0 0 16px" }}>
                      💡 <strong>طريقة الحفظ كـ PDF:</strong> اضغط &quot;افتح الدليل&quot; ← سيفتح في تبويب جديد ←
                      اضغط زر <strong>&quot;🖨️ طباعة / حفظ PDF&quot;</strong> في أعلى الصفحة ← اختر &quot;Save as PDF&quot;.
                    </p>
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/system-manual`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="open-system-manual-btn"
                      style={{
                        display: "inline-block",
                        padding: "10px 22px",
                        background: "linear-gradient(135deg, #8b5a2b, #5c3a1e)",
                        color: "#fff",
                        borderRadius: 8,
                        textDecoration: "none",
                        fontSize: 13,
                        fontWeight: 700,
                        boxShadow: "0 2px 8px rgba(139,90,43,0.25)",
                      }}
                    >
                      📖 افتح الدليل الآن
                    </a>
                  </div>
                </CollapsibleSection>

                {/* ─── قسم: قوالب الطلبات والموافقات (أدمن فقط) ─── */}
                {hasPerm("settings.request_templates") && (
                  <CollapsibleSection
                    id="req-templates"
                    icon="🗂️"
                    title="إعدادات الطلبات والموافقات"
                    subtitle="قوالب الطلبات · حقول مخصصة · سلسلة الموافقات لكل نوع"
                    defaultOpen={false}
                  >
                    <Suspense fallback={<div style={{ padding: 14, fontSize: 12, color: "var(--muted)" }}>…</div>}>
                      <RequestTemplatesPanel />
                    </Suspense>
                  </CollapsibleSection>
                )}

                {/* ─── إدارة المستخدمين والصلاحيات (موحَّد) ─── */}
                <CollapsibleSection
                  id="users-perm"
                  icon="👥"
                  title="المستخدمون والصلاحيات"
                  subtitle={user.role === "admin"
                    ? "إدارة المستخدمين · مصفوفة الأدوار × الصلاحيات · حسابي"
                    : "كلمة السر · المصادقة الثنائية · مشاركة البيانات"}
                  defaultOpen={false}
                >
                  <UsersAndPermissions currentUser={user} />
                </CollapsibleSection>

                {/* v119.90 — Removed the old «التكامل مع الأنظمة الخارجية»
                    section (was IntegrationsSettings). Its status info is
                    now redundant with the new «مركز التكاملات الخارجية»
                    above. The old test-send tools were rarely used and
                    can be re-added as a separate tool if needed. */}

                {/* ─── القسم 2: الإشعارات والأهداف والإيميل ─── */}
                {hasPerm("settings.notifications") && (
                  <CollapsibleSection
                    id="notify-goals"
                    icon="🎯"
                    title="الأهداف والإشعارات"
                    subtitle="الأهداف الشهرية · بَونات · إشعارات الأخطاء · الإيميل اليومي · الفرع"
                    defaultOpen={false}
                  >
                    {user.role === "admin" && <GoalsPanel />}
                    {user.role === "admin" && <DigestPanel />}
                    {user.role === "admin" && <AdminNotifyPanel />}
                    {/* v-caps 2026-09: قائمة استبعاد واتساب انتقلت إلى مركز التواصل →
                        WhatsApp → 📤 الإرسال → 🚫 قائمة الاستبعاد (صلاحية whatsapp.optout.manage). */}
                    {/* v139: BranchPanel (إعدادات البونص) انتقلت إلى الموظفين ← تبويب البونص */}
                    <PushSettings />
                  </CollapsibleSection>
                )}

                {/* ─── النسخ الاحتياطية ─── */}
                {hasPerm("settings.backup") && (
                  <CollapsibleSection
                    id="data-backup"
                    icon="💾"
                    title="النسخ الاحتياطية"
                    subtitle="تصدير واستيراد البيانات · مسح شامل"
                    defaultOpen={false}
                  >
                    <div className="set-sec">
                      <p style={{ fontSize: 12, color: "var(--gy)", marginBottom: 12, lineHeight: 1.8 }}>
                        البيانات محفوظة تلقائياً على السحابة وتُستعاد فور تسجيل الدخول من أي جهاز.<br />
                        التصدير/الاستيراد لمشاركة نسخ احتياطية.
                      </p>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <button data-testid="btn-export-json" className="btn btn-success" onClick={exportJSON}>📤 تصدير البيانات</button>
                        {(canUpload.inv || canUpload.cust || canUpload.prod) && (
                          <button data-testid="btn-import-json" className="btn btn-primary" onClick={() => fjRef.current?.click()}>📥 استيراد</button>
                        )}
                        {user.role === "admin" && (
                          <button data-testid="btn-clear-all" className="btn" style={{ background: "var(--rd)", color: "#fff" }} onClick={clearAll}>🗑 مسح الكل</button>
                        )}
                      </div>
                      {stMsg && <div style={{ marginTop: 10, fontSize: 12, color: stMsg.t === "g" ? "var(--gn)" : "var(--rd)" }}>{stMsg.m}</div>}
                    </div>
                  </CollapsibleSection>
                )}

                {/* ─── v119.209 — صناديق الصلاحيات (iOS Role Boxes) ─── */}
                {user.role === "admin" && (
                  <CollapsibleSection
                    id="perm-boxes"
                    icon="🧩"
                    title="صناديق الصلاحيات"
                    subtitle="تفعيل/تعطيل مجموعات الصلاحيات لكل دور بضغطة واحدة — بيانات · عمليات · تسويق · أكاديمية · نظام"
                    defaultOpen={false}
                  >
                    <Suspense fallback={<div style={{ padding: 14 }}>⏳ تحميل...</div>}>
                      <PermissionBoxesPanel />
                    </Suspense>
                  </CollapsibleSection>
                )}

                {/* ─── v119.209 — محرك الإجراءات الذكي (SOP Engine) ─── */}
                {(user.role === "admin" || user.role === "ceo") && (
                  <CollapsibleSection
                    id="sop-engine"
                    icon="🔄"
                    title="محرك الإجراءات الذكي (SOP)"
                    subtitle="ارفع ملف الإجراءات — النظام يدرسه ويحوّله آلياً لسلاسل موافقات (يبدأ · يعتمد · يقيّم)"
                    defaultOpen={false}
                  >
                    <Suspense fallback={<div style={{ padding: 14 }}>⏳ تحميل...</div>}>
                      <SOPEnginePanel />
                    </Suspense>
                  </CollapsibleSection>
                )}
              </SettingsCatProvider>
            )}
          </div>
          );
        })()}
        </div>
        </PanelErrorBoundary>
      </div>

      {/* v11 (2026-02-17) — Iter41: single mobile nav system.
          MobileNavDrawer (top ☰ + slide-out) + MobileBottomBar (.mbb) only.
          Bottom padding driven by --app-bottom-offset in index.css (no literal). */}
      <InstallAppPrompt />
      <MobileBottomBar
        currentTab={tab}
        onNav={(id) => setTab(id)}
        onOpenNotif={() => window.dispatchEvent(new CustomEvent("tw-open-notif"))}
        onOpenMenu={() => window.dispatchEvent(new CustomEvent("tw-open-mob-drawer"))}
      />
    </>
  );
}

function Shell() {
  const { authed } = useAuth();
  useTheme(); // applies persisted theme to <html data-theme>
  if (authed === undefined) {
    return (
      <>
      <SplashScreen />
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", fontFamily: "Tajawal, sans-serif" }}>
        <div style={{ textAlign: "center" }}>
          <img src="/thawany-logo.png" alt="ثواني" style={{ height: 50, marginBottom: 14 }} />
          <div style={{ fontSize: 12, color: "var(--gy)" }}>⏳ جارٍ التحقق من الجلسة...</div>
        </div>
      </div>
      </>
    );
  }
  if (!authed) return <><SplashScreen /><Login /></>;
  return (
    <>
      <SplashScreen />
      <Dashboard />
      <Toaster />
      {/* v119.75 — sonner toaster (used by WhatsApp/CS panels). Wrapped so
          tests can poll `[data-testid="wa-toast"]` for any notification. */}
      <div data-testid="wa-toast">
        <SonnerToaster position="bottom-end" richColors closeButton dir="rtl"
          toastOptions={{ className: "wa-toast-item" }} />
      </div>
    </>
  );
}

export default function App() {
  // Public portal route — completely bypasses auth so external partners can upload.
  if (typeof window !== "undefined" && window.location.pathname.startsWith("/portal/")) {
    return (
      <Suspense fallback={<div style={{ padding: 40, textAlign: "center" }}>...</div>}>
        <PortalUpload />
      </Suspense>
    );
  }
  // /s/{token} — رابط استبيان ← صفحة الاستبيان، رابط شريك ← بوابة الشركاء، غير ذلك ← «غير متاح».
  if (typeof window !== "undefined" && window.location.pathname.startsWith("/s/")) {
    return (
      <Suspense fallback={<div style={{ padding: 40, textAlign: "center" }}>...</div>}>
        <ShortLinkRouter />
      </Suspense>
    );
  }
  // Ambassador public portal — completely separate auth (code + PIN), read-only.
  if (typeof window !== "undefined" && window.location.pathname.startsWith("/ambassador")) {
    return (
      <Suspense fallback={<div style={{ padding: 40, textAlign: "center" }}>...</div>}>
        <AmbassadorPortal />
      </Suspense>
    );
  }
  // v119.72 — Customer Service mobile inbox (PIN-based auth, no main login)
  if (typeof window !== "undefined" && window.location.pathname.startsWith("/cs-inbox")) {
    return (
      <Suspense fallback={<div style={{ padding: 40, textAlign: "center" }}>...</div>}>
        <CsInboxApp />
        {/* v119.75 — sonner toaster on the public CS inbox too. */}
        <div data-testid="wa-toast">
          <SonnerToaster position="bottom-center" richColors closeButton dir="rtl" />
        </div>
      </Suspense>
    );
  }
  // v119.202 — Branch iPad Screen (PIN-based, LPR + shift tasks)
  if (typeof window !== "undefined" && window.location.pathname.startsWith("/branch-screen")) {
    return (
      <Suspense fallback={<div style={{ padding: 40, textAlign: "center" }}>...</div>}>
        <BranchScreen />
      </Suspense>
    );
  }
  // v119.204 — Public 360 life-quality survey (WhatsApp token link)
  if (typeof window !== "undefined" && window.location.pathname.startsWith("/survey360/")) {
    return (
      <Suspense fallback={<div style={{ padding: 40, textAlign: "center" }}>...</div>}>
        <Survey360Public />
      </Suspense>
    );
  }
  return (
    <AuthProvider>
      <UpdateBanner />
      <JustUpdatedToast />
      <VersionBadge />
      <Shell />
    </AuthProvider>
  );
}
